// HU-12 · HU-15 · HU-43 · La evaluación del Comité (F-03, Sprint 2).
//
// Seis pestañas sobre UNA lectura (`GET /evaluacion/{caso}`):
//   · Investigación   los 29 puntos del instructivo en 6 bloques, contador «n de 28», el
//                     26 sin respuesta, el 23 derivado del 22, marcado en bloque con razón
//                     y su deshacer (HU-12 C5–C13). Guardar con pendientes AVISA y deja
//                     guardar (C7); se guarda SOLO lo que cambió (guardado parcial), y
//                     por eso BORRAR un punto viaja aparte, en `puntos_retirados`: en un
//                     guardado parcial lo ausente significa «no lo toqué» (Bug 892).
//   · Causalidad      la escala exacta del Formato Amarillo (C1), el algoritmo
//                     PRESELECCIONADO desde el parámetro y cambiable (C2), la
//                     justificación obligatoria (C14) y la constancia «Algoritmo
//                     aplicado: …» (C3).
//   · Gravedad        la guía con el criterio vigente (HU-15-C1), el criterio del
//                     registrador SOLO informativo (C3), el campo vacío hasta que el
//                     Comité decide; grave habilita «Ampliar informe (act. 8)» (C4), no
//                     grave marca «Incluir en el próximo IPS» (C6); cambiar exige
//                     justificación (C9).
//   · Especialistas   invitar (sugeridos primero los del país del evento, C2), retirar,
//                     marcar «Participó en el análisis» (C3) y los aportes (C9–C11).
//   · Informe FAR-03  HU-14: elaborar (borrador), las ocho secciones compuestas desde la
//                     evaluación, redactar Antecedentes/Demanda/Conclusión, enviar a
//                     aprobación (inmutable), versiones (`InformeFar03.tsx`). Con
//                     `puedeAprobar` (el aprobador), la tarjeta «Aprobación».
//   · Acciones        HU-13: tipo (exactamente inmediata / preventiva / correctiva, C2),
//                     «Qué se hace» del catálogo `clase_accion` u «Otra acción» libre (C4/C5),
//                     responsable y plazo obligatorios con su mensaje bajo el campo (C7/C7b),
//                     observaciones opcionales (C1), el estado de cada acción por separado
//                     (C3) y la sección del FAR-03 que le toca (C6/C8).
//
// SOLO LECTURA (HU-11-C21, HU-43-C7): con `puede_editar=false` —el invitado, AR, el
// aprobador— no hay «Guardar evaluación» ni controles; el instructivo y la causalidad se
// muestran tal cual. El invitado tiene además «Agregar observación» y «Adjuntar
// documento», que son SUYOS y no tocan la evaluación.
//
// LO QUE RESPONDE UNA MUTACIÓN SE FUSIONA, NO SE PISA (D2, 14-sep-2026). Ver `fusionar`
// en `api/evaluacion.ts`: la cabecera del caso y lo que la respuesta no trae se conservan
// de la lectura previa, y después se RECARGA la evaluación entera para tener la verdad
// del servidor (el predeterminado, la constancia, el aviso de ampliación).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { comoFechaDeInstante, comoFechaYHora } from '../lib/fechas';
import type { ChangeEvent, ReactNode } from 'react';
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ClipboardList, FileText, FileUp, Info, ListChecks, Lock, Mail, MessageSquarePlus,
  PenLine, Save, Scale, Stethoscope, Trash2, UserMinus, UserPlus, Users, Download, Paperclip,
} from 'lucide-react';
import {
  Alert, Button, Card, Checkbox, Field, Input, L2Tag, Modal, Mono, PageHeader, Select, StatusBadge, tamanoLegible,
  Tabs, Textarea,
} from '../components/ui';
import type { Pestana } from '../components/ui';
import Instructivo, { RESPONDIBLES, PUNTO_DERIVADO, PUNTO_ORIGEN, OBSERVACION_DERIVADA, cierraLaDerivacion, elDerivadoSeRespondeAMano } from '../components/Instructivo';
import type { RespuestaDelPunto } from '../components/Instructivo';
import InformeFar03 from '../components/InformeFar03';
import AprobacionDelInforme from '../components/AprobacionDelInforme';
import type { DecisionDelAprobador } from '../components/AprobacionDelInforme';
import AmpliacionDelInforme from '../components/AmpliacionDelInforme';
import { INSTRUCTIVO } from '../data/instructivo';
import Bandera from '../components/Banderas';
import {
  ESTADOS_DE_ACCION, GRADOS_DE_CAUSALIDAD, TIPOS_DE_ACCION, adjuntarComoEspecialista, agregarAccion,
  cambiarEstadoDeAccion, decidirGravedad, descargarAporte, deshacerMarcado,
  etiquetaDelCriterio, fusionar, guiaDeGravedad, invitarEspecialista, listarAlgoritmos, marcarEnBloque,
  marcarParticipacion, observarComoEspecialista, quitarAccion, registrarCausalidad, responderInstructivo,
  mensajeDeTopeSuperado, retirarAporte, retirarEspecialista, sugerirEspecialistas, topeDeTexto, verEvaluacion, verInforme,
  TOPE_DE_TEXTO_LIBRE_DEL_COMITE,
} from '../api/evaluacion';
import type { Accion, Aporte, EspecialistaSugerido, Evaluacion, GuiaDeGravedad, Informe, Invitacion, ValorDeRespuesta } from '../api/evaluacion';
import { listarCatalogo } from '../api/catalogos';
import { archivoABase64 } from '../api/plantillas';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { normalizarCorreo, revisarCorreo } from '../lib/validacion';
import { soloCorreo, soloNombreDePersona } from '../lib/entradas';
import { useDiferido } from '../lib/useDiferido';
import { useCatalogosOpcional, useZonaDelPais } from '../lib/CatalogosContexto';
import { nombreComparable, problemaDelAnexo, textoDeLosLimites as textoDeLosLimitesDelAnexo } from '../lib/anexos';
import { descargarBlob } from '../lib/entradas';
import { etiquetaDelEstadoDelCaso } from '../lib/etiquetas';

// Etiqueta de pantalla <-> código del API. Los descriptivos leen la terna como
// «Documentado / No se pudo obtener / No aplica» (ver `Instructivo.tsx`).
// Las esperas, en milisegundos, entre un reintento y el siguiente al aguardar a que el
// caso cambie de estado. Cubren ~8 s en total: lo MEDIDO contra la base de Azure desde
// una máquina local (6,59 s en el sobre de PE-224-2026), con margen. En el publicado la
// API corre al lado de la base y se resuelve en los primeros intentos; los demás no
// llegan a ejecutarse. Ver `recargarEsperandoCambioDeEstado`.
const ESPERAS_HASTA_QUE_EL_CASO_CAMBIE = [0, 300, 700, 1500, 2500, 3000] as const;

const A_CODIGO: Record<string, ValorDeRespuesta> = {
  'Sí': 'si', 'No': 'no', 'No aplica': 'no_aplica',
  'Documentado': 'si', 'No se pudo obtener': 'no',
};
const A_ETIQUETA = (punto: number, valor: ValorDeRespuesta | undefined): string => {
  if (!valor) return '';
  const p = INSTRUCTIVO.find((x) => x.n === punto);
  if (valor === 'no_aplica') return 'No aplica';
  if (p?.descriptivo) return valor === 'si' ? 'Documentado' : 'No se pudo obtener';
  return valor === 'si' ? 'Sí' : 'No';
};

type RespuestaLocal = RespuestaDelPunto;

/**
 * BUG-898 (`ui/MG-ILE-898.md`, Estados): tras un 422 de una escritura congelada —la pestaña
 * se abrió antes de enviar el informe— la pantalla muestra el texto del servidor y SE
 * REFRESCA desde él, para que la evaluación pase a solo lectura sin recargar la página
 * (§8.1 R3). Cada pestaña lo pide por aquí en lugar de recibir otra prop.
 */
const RecargarLaEvaluacion = createContext<() => void>(() => undefined);

/** Un 422 de escritura: se pinta el texto del servidor y se relee la evaluación. */
function useAlRechazar() {
  const recargar = useContext(RecargarLaEvaluacion);
  return (f: Fallo): Fallo => {
    if (f.estado === 422) recargar();
    return f;
  };
}

// DD/MM/AAAA EN TODA LA INTERFAZ (25-sep-2026). Antes recortaba el ISO y lo mostraba
// tal cual, asi que en la misma fila convivian «2026-09-24» y «25/09/26». RN-82: el día se
// toma en la zona del país del caso (`useZonaDelPais`).
const fecha = (iso: unknown, zona: string | null): string => comoFechaDeInstante(iso, zona) || '—';

/** El instructivo en SOLO LECTURA (HU-12-C15, HU-43-C7): los 29 puntos con su respuesta. */
function InstructivoSoloLectura({ evaluacion }: { evaluacion: Evaluacion }) {
  const porPunto = new Map(evaluacion.respuestas.map((r) => [r.punto, r]));
  return (
    <Card data-testid="evaluacion-investigacion-solo-lectura" pad={false} title={`Investigación de causalidad · ${evaluacion.respondidos} de ${evaluacion.total} respondidos`}
      icon={Lock} subtitle="Solo lectura: no puedes responder el instructivo ni cambiar la causalidad.">
      <ol className="divide-y divide-hairline" aria-label="Instructivo en solo lectura">
        {INSTRUCTIVO.map((p) => {
          const r = porPunto.get(p.n);
          return (
            <li key={p.n} className="px-4 py-2 flex flex-wrap items-start gap-2 text-meta">
              <Mono className="text-meta w-6 shrink-0">{String(p.n).padStart(2, '0')}</Mono>
              <span className="min-w-0 flex-1">{p.corto}</span>
              {p.nota === 'clausula'
                ? <StatusBadge tone="neutral">Cláusula · no se responde</StatusBadge>
                : <StatusBadge tone={r ? 'info' : 'neutral'}>{r ? A_ETIQUETA(p.n, r.valor) : 'Sin responder'}</StatusBadge>}
              {r?.observacion && (
                <span className={'basis-full text-meta pl-8 ' + (r.observacion_protegida ? 'text-rose-700' : 'text-ink-soft')}>
                  {r.observacion_protegida ? <span className="inline-flex items-center gap-1"><Lock size={11} /> {r.observacion}</span> : r.observacion}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

/**
 * Cada pestaña con formulario avisa al padre si tiene cambios sin guardar (18-sep-2026,
 * observación del operador): antes, cambiar de pestaña desmontaba el formulario y lo
 * escrito se perdía sin aviso —y la lista de faltantes del FAR-03 seguía, con razón,
 * diciendo que faltaba lo que nunca se guardó—.
 */
type AvisaCambios = { onSucio?: (sucio: boolean) => void };

function PestanaInvestigacion({ evaluacion, onCambio, actor, onSucio }: {
  evaluacion: Evaluacion; onCambio: (e: Evaluacion) => void; actor: string;
} & AvisaCambios) {
  // INTEGRIDAD L2 (HU-33): una observación protegida llega ENMASCARADA
  // («[dato protegido…]»). No se pinta como texto editable —se marca `protegida` y el
  // instructivo muestra la insignia— y al guardar NO se reenvía: si viajara la máscara,
  // pisaría el dato real en el servidor.
  const desdeServidor = useCallback((e: Evaluacion): Record<number, RespuestaLocal> =>
    Object.fromEntries(e.respuestas.map((r) => [r.punto, {
      valor: A_ETIQUETA(r.punto, r.valor),
      obs: r.observacion_protegida ? '' : (r.observacion ?? ''),
      protegida: r.observacion_protegida,
      derivada: r.derivada,
    }])), []);
  const [respuestas, setRespuestas] = useState<Record<number, RespuestaLocal>>(() => desdeServidor(evaluacion));
  const [tocados, setTocados] = useState<Set<number>>(new Set());
  useEffect(() => { onSucio?.(tocados.size > 0); }, [tocados, onSucio]);
  const [aviso, setAviso] = useState<number | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const alRechazar = useAlRechazar();
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => { setRespuestas(desdeServidor(evaluacion)); setTocados(new Set()); }, [evaluacion, desdeServidor]);

  /**
   * LO QUE EL SERVIDOR TIENE GUARDADO AHORA MISMO (Bug 892). Es la referencia para saber
   * si un borrado RETIRA algo o no retira nada: un punto que nunca se respondió no es un
   * retiro —es un punto sin responder—, y mandarlo como retirado ensucia la auditoría de
   * un expediente regulado.
   */
  const guardadosEnElServidor = useMemo(
    () => new Set(evaluacion.respuestas.filter((r) => r.valor).map((r) => r.punto)),
    [evaluacion],
  );

  const pendientes = useMemo(() => RESPONDIBLES.filter((p) => !respuestas[p.n]?.valor).length, [respuestas]);

  const responder = (n: number, campo: 'valor' | 'obs', valor: string) => {
    setRespuestas((r) => {
      // BORRAR RETIRA LA RESPUESTA ENTERA, NO SOLO LA OPCIÓN (Bug 892). La observación es
      // parte de lo respondido y se va con ella: dejarla huérfana pintaba una nota bajo un
      // punto sin responder. Sobrevive solo la marca L2, para que volver a responder sin
      // escribir nada no reenvíe `observacion: null` y borre el dato protegido del
      // servidor (HU-33) — ahí no hubo retiro, hubo cambio de respuesta.
      const retirado: RespuestaLocal = r[n]?.protegida ? { protegida: true } : {};
      const siguiente: Record<number, RespuestaLocal> = campo === 'valor' && !valor
        ? { ...r, [n]: retirado }
        : { ...r, [n]: { ...r[n], [campo]: valor } };
      // HU-12-C11 (Bug 712): el 23 se deriva del 22 AQUI, no solo al guardar. «No» o «No
      // aplica» en el 22 lo ponen en «No aplica» con el texto literal de la HU; «Sí» (o
      // borrar) retiran una derivacion previa y lo dejan pendiente. Es la misma regla
      // que aplica el servidor (`_derivar_desde`), para que la pantalla no la contradiga.
      if (n === PUNTO_ORIGEN && campo === 'valor') {
        if (cierraLaDerivacion(valor)) {
          siguiente[PUNTO_DERIVADO] = { valor: 'No aplica', obs: OBSERVACION_DERIVADA, derivada: true };
        } else if (siguiente[PUNTO_DERIVADO]?.derivada) {
          delete siguiente[PUNTO_DERIVADO];
        }
      }
      return siguiente;
    });
    setTocados((t) => new Set(t).add(n));
  };

  /** Lo respondido y aún no guardado, en la forma del API (guardado parcial). */
  // El 23 viaja SOLO cuando lo respondio una persona con el 22 en «Sí» (HU-12-C11
  // completada). Derivado no viaja: el servidor lo deriva del 22 en el mismo guardado y
  // rechaza una respuesta directa (422), que antes tumbaba el PATCH entero (Bug 712).
  const viaja = (n: number) => n !== PUNTO_DERIVADO
    || (!respuestas[n]?.derivada && elDerivadoSeRespondeAMano(respuestas[PUNTO_ORIGEN]?.valor));
  const cambiosPendientes = () => [...tocados]
    .filter((n) => viaja(n) && respuestas[n]?.valor)
    .map((n) => {
      const r = respuestas[n];
      const cambio: { punto: number; valor: ValorDeRespuesta; observacion?: string | null } = { punto: n, valor: A_CODIGO[r.valor ?? ''] };
      // Protegida y sin texto nuevo: la observación NO viaja; el servidor conserva la suya.
      if (!(r.protegida && !r.obs)) cambio.observacion = r.obs || null;
      return cambio;
    });

  /**
   * LO BORRADO A PROPÓSITO EN ESTA SESIÓN (Bug 892). Va aparte de `respuestas` porque el
   * guardado es parcial: lo que no viaja significa «no lo toqué», nunca «bórralo».
   *
   * Tres filtros, y cada uno protege algo distinto:
   *   · `tocados` — solo lo que una PERSONA tocó. Deja fuera el 23 cuando la derivación
   *     se retira sola al cambiar el 22 (HU-12-C11): eso no lo borró nadie, y el
   *     servidor lo vuelve a derivar en este mismo guardado.
   *   · `viaja` — la misma regla del envío de respuestas: el derivado no se manipula a
   *     mano; el servidor rechaza tocarlo (422).
   *   · `guardadosEnElServidor` — LA DISTINCIÓN QUE IMPORTA: solo es retiro lo que
   *     existía. Responder y borrar antes de guardar no retira nada porque el servidor
   *     nunca lo supo; mandarlo dejaría en el expediente el rastro de un borrado que
   *     nunca ocurrió.
   *
   * Un punto con valor no llega aquí, así que ninguno puede caer en las dos listas.
   */
  const retiradosPendientes = () => [...tocados]
    .filter((n) => viaja(n) && !respuestas[n]?.valor && guardadosEnElServidor.has(n))
    .sort((a, b) => a - b);

  const guardar = async () => {
    setFallo(null);
    setAviso(null);
    const cambios = cambiosPendientes();
    const retirados = retiradosPendientes();
    // Las dos listas vacías son un guardado sin contenido: el servidor lo rechaza con 422
    // y aquí ni se pide (p. ej. responder y borrar el mismo punto sin guardar entremedio).
    if (!cambios.length && !retirados.length) return;
    setOcupado(true);
    try {
      onCambio(await responderInstructivo(evaluacion.caso_id, cambios, retirados));
    } catch (e) {
      setFallo(alRechazar(aFallo(e)));
    } finally {
      setOcupado(false);
    }
  };

  // C7: con puntos sin responder se AVISA y se deja guardar.
  const intentarGuardar = () => {
    if (pendientes > 0 && aviso === null) { setAviso(pendientes); return; }
    guardar();
  };

  // Devuelve si el servidor confirmó: el instructivo solo ofrece «Deshacer» entonces.
  const enBloque = async (puntos: number[], _valor: string, razon: string): Promise<boolean> => {
    setFallo(null);
    if (!razon.trim()) { setFallo({ message: 'La razón del grupo es obligatoria.' }); return false; }
    setOcupado(true);
    try {
      // LO RESPONDIDO Y NO GUARDADO SE GUARDA PRIMERO. La respuesta del marcado en bloque
      // sustituye el estado local entero: sin este paso, los puntos contestados a mano
      // desde el último «Guardar» se perdían al confirmar el bloque (QA, 14-sep-2026).
      // Los BORRADOS van en el mismo viaje por lo mismo (Bug 892): si no, el bloque los
      // ve como pendientes, los marca «No aplica» y el retiro se pierde sin rastro.
      const pendientes = cambiosPendientes();
      const retirados = retiradosPendientes();
      if (pendientes.length || retirados.length) {
        await responderInstructivo(evaluacion.caso_id, pendientes, retirados);
      }
      onCambio(await marcarEnBloque(evaluacion.caso_id, puntos, razon));
      return true;
    } catch (e) {
      setFallo(alRechazar(aFallo(e)));
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const deshacer = async (_puntos: number[], razon: string) => {
    setFallo(null);
    setOcupado(true);
    try {
      onCambio(await deshacerMarcado(evaluacion.caso_id, razon));
    } catch (e) {
      setFallo(alRechazar(aFallo(e)));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <>
      {fallo && <div className="mb-3"><Alert data-testid="evaluacion-error-envio" tone="danger" title="No se pudo guardar" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert></div>}
      <Instructivo respuestas={respuestas} onResponder={responder} onResponderVarios={enBloque}
        onDeshacerBloque={(p, r) => { deshacer(p, r); }} ocupado={ocupado} />
      {/* La fila de guardar va FIJA al pie: con 29 puntos a la vista, el botón quedaba a
          dos pantallas de donde se estaba respondiendo. */}
      <div className="sticky bottom-0 z-10 mt-3 -mx-1 px-1 py-2 bg-canvas/95 backdrop-blur-none border-t border-hairline flex flex-wrap items-center gap-3">
        {aviso !== null ? (
          <Alert tone="warning" title={`Quedan ${aviso} puntos sin responder`}>
            <div className="mt-2 flex gap-2">
              <Button size="sm" icon={Save} onClick={() => { guardar(); }} disabled={ocupado}>Guardar de todos modos</Button>
              <Button size="sm" variant="secondary" onClick={() => setAviso(null)}>Seguir respondiendo</Button>
            </div>
          </Alert>
        ) : (
          <Button data-testid="evaluacion-investigacion-guardar" icon={Save} onClick={intentarGuardar} disabled={ocupado || tocados.size === 0}>Guardar evaluación</Button>
        )}
        <span className="text-meta text-ink-soft">Evalúa: {actor}</span>
        {tocados.size > 0 && <span className="text-meta text-ink-soft">· {tocados.size} {tocados.size === 1 ? 'punto sin guardar' : 'puntos sin guardar'}</span>}
      </div>
    </>
  );
}

// El "120 / 500 caracteres" de los campos con tope; sin tope, nada (como RegistroCaso).
const cuentaAtras = (valor: string, tope?: number): string | undefined =>
  (tope ? `${valor.length} / ${tope} caracteres` : undefined);

function PestanaCausalidad({ evaluacion, onCambio, actor, onSucio }: {
  evaluacion: Evaluacion; onCambio: (e: Evaluacion) => void; actor: string;
} & AvisaCambios) {
  const alRechazar = useAlRechazar();
  const [algoritmos, setAlgoritmos] = useState<{ codigo: string; nombre: string; activo: boolean }[]>([]);
  const [grado, setGrado] = useState(evaluacion.grado_causalidad ?? '');
  // C2: llega PRESELECCIONADO el predeterminado (o el ya guardado), y se puede cambiar.
  const [algoritmo, setAlgoritmo] = useState(evaluacion.algoritmo ?? evaluacion.algoritmo_predeterminado ?? '');
  const [justificacion, setJustificacion] = useState(evaluacion.justificacion ?? '');
  const [conclusiones, setConclusiones] = useState(evaluacion.conclusiones ?? '');
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [guardado, setGuardado] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  // QA (18-sep-2026): el mensaje rojo se va en cuanto el usuario escribe en el campo; no
  // se queda hasta el siguiente «Guardar». Mismo trato que la pestaña Acciones (Bug 716).
  const limpiarError = (campo: string) =>
    setErrores((e) => {
      if (!(campo in e)) return e;
      const resto = { ...e };
      delete resto[campo];
      return resto;
    });
  // HU-12: 500 por defecto en la base, configurables en `parametro` (HU-31) sin desplegar.
  const topeDeJustificacion = topeDeTexto(evaluacion, 'justificacion_caracteres');
  const topeDeConclusion = topeDeTexto(evaluacion, 'conclusion_caracteres');
  // Sucio = lo escrito difiere de lo guardado (lo que se envía va recortado, se compara igual).
  const sucio = grado !== (evaluacion.grado_causalidad ?? '')
    || justificacion.trim() !== (evaluacion.justificacion ?? '').trim()
    || conclusiones.trim() !== (evaluacion.conclusiones ?? '').trim()
    || (algoritmo || '') !== (evaluacion.algoritmo ?? evaluacion.algoritmo_predeterminado ?? '');
  useEffect(() => { onSucio?.(sucio); }, [sucio, onSucio]);

  useEffect(() => {
    let vivo = true;
    listarAlgoritmos()
      .then((lista) => { if (vivo) setAlgoritmos(lista.filter((a) => a.activo)); })
      .catch(() => { if (vivo) setAlgoritmos([]); });
    return () => { vivo = false; };
  }, []);

  // Si la recarga trae el predeterminado que la respuesta de la mutación no traía, el
  // desplegable lo recoge (solo mientras el usuario no haya elegido otro).
  useEffect(() => {
    if (!algoritmo && (evaluacion.algoritmo || evaluacion.algoritmo_predeterminado)) {
      setAlgoritmo(evaluacion.algoritmo ?? evaluacion.algoritmo_predeterminado ?? '');
    }
  }, [evaluacion.algoritmo, evaluacion.algoritmo_predeterminado, algoritmo]);

  const guardar = async () => {
    const problemas: Record<string, string> = {};
    if (!grado) problemas.grado = 'Elige la causalidad en la escala del Formato Amarillo.';
    // C14: la justificación es obligatoria.
    if (!justificacion.trim()) problemas.justificacion = 'La justificación es obligatoria';
    // El tope se exige también aquí: `maxLength` no cubre un texto ya guardado más largo
    // que el tope vigente (la base puede bajarlo después), y así el aviso sale bajo el campo.
    else if (topeDeJustificacion && justificacion.trim().length > topeDeJustificacion) {
      problemas.justificacion = mensajeDeTopeSuperado('La justificación', justificacion.trim().length, topeDeJustificacion);
    }
    // Bug 713 (ADO): la conclusión es la sección 5 del FAR-03 y el backend la exige como
    // bloqueante al elaborar el informe; sin validarla aquí, el Comité recién se enteraba
    // al pedir el informe. Se valida donde se escribe.
    if (!conclusiones.trim()) problemas.conclusiones = 'La conclusión del Comité es obligatoria: es la sección 5 del FAR-03';
    else if (topeDeConclusion && conclusiones.trim().length > topeDeConclusion) {
      problemas.conclusiones = mensajeDeTopeSuperado('La conclusión del Comité', conclusiones.trim().length, topeDeConclusion);
    }
    setErrores(problemas);
    if (Object.keys(problemas).length) return;
    setFallo(null);
    setOcupado(true);
    try {
      onCambio(await registrarCausalidad(evaluacion.caso_id, {
        grado, justificacion: justificacion.trim(), algoritmo: algoritmo || null, evaluado_por: actor,
        conclusiones: conclusiones.trim(),
      }));
      setGuardado(true);
    } catch (e) {
      setFallo(alRechazar(aFallo(e)));
    } finally {
      setOcupado(false);
    }
  };

  const soloLectura = !evaluacion.puede_editar;

  return (
    <Card title="Causalidad y conclusiones" icon={Scale} subtitle="La escala del Formato Amarillo; queda constancia del algoritmo con el que se clasificó.">
      {evaluacion.constancia_del_algoritmo && (
        <div className="mb-3"><StatusBadge tone="info" icon={Info}>{evaluacion.constancia_del_algoritmo}</StatusBadge></div>
      )}
      {soloLectura ? (
        <dl data-testid="evaluacion-causalidad-solo-lectura" className="grid grid-cols-1 md:grid-cols-2 gap-3 text-meta">
          <div><dt className="text-ink-soft">Causalidad</dt><dd>{GRADOS_DE_CAUSALIDAD.find((g) => g.value === evaluacion.grado_causalidad)?.label ?? '—'}</dd></div>
          <div><dt className="text-ink-soft">Algoritmo aplicado</dt><dd>{evaluacion.algoritmo ?? '—'}</dd></div>
          <div className="md:col-span-2"><dt className="text-ink-soft">Justificación</dt><dd>{evaluacion.justificacion ?? '—'}</dd></div>
          <div className="md:col-span-2"><dt className="text-ink-soft">Conclusión del Comité</dt><dd>{evaluacion.conclusiones ?? '—'}</dd></div>
        </dl>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Causalidad" required error={errores.grado}>
              <Select aria-label="Causalidad" placeholder="Elegir…" value={grado}
                onChange={(e) => { setGrado(String(e.target.value ?? '')); limpiarError('grado'); }}
                options={GRADOS_DE_CAUSALIDAD.map((g) => ({ value: g.value, label: g.label }))} />
            </Field>
            <Field label="Algoritmo aplicado" hint={evaluacion.algoritmo ? 'Ya guardado: se conserva salvo que lo cambies.' : 'Preseleccionado desde el predeterminado. Puedes cambiarlo antes de guardar.'}>
              <Select aria-label="Algoritmo aplicado" placeholder="Elegir…" value={algoritmo}
                onChange={(e) => setAlgoritmo(String(e.target.value ?? ''))}
                options={algoritmos.map((a) => ({ value: a.codigo, label: a.nombre }))} />
            </Field>
          </div>
          <Field label="Justificación" required error={errores.justificacion} hint="Sin sustento la clasificación no sirve como prueba."
            contador={cuentaAtras(justificacion, topeDeJustificacion)}>
            <Textarea aria-label="Justificación" rows={3} value={justificacion} maxLength={topeDeJustificacion}
              onChange={(e) => { setJustificacion(e.target.value); limpiarError('justificacion'); }} />
          </Field>
          <Field label="Conclusión del Comité" required error={errores.conclusiones} hint="Sección 5 del FAR-03."
            contador={cuentaAtras(conclusiones, topeDeConclusion)}>
            <Textarea aria-label="Conclusión del Comité" rows={3} value={conclusiones} maxLength={topeDeConclusion}
              onChange={(e) => { setConclusiones(e.target.value); limpiarError('conclusiones'); }} />
          </Field>
          {fallo && <div className="mb-3"><Alert data-testid="evaluacion-error-envio" tone="danger" title="No se pudo guardar" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert></div>}
          {guardado && <div className="mb-3"><Alert tone="success" title="Evaluación guardada" onCerrar={() => setGuardado(false)} /></div>}
          <Button data-testid="evaluacion-causalidad-guardar" icon={Save} onClick={() => { guardar(); }} disabled={ocupado}>Guardar evaluación</Button>
        </>
      )}
    </Card>
  );
}

/** «Causalidad y conclusiones» está completa cuando la causalidad y la conclusión del Comité están guardadas. */
export const causalidadCompleta = (e: Pick<Evaluacion, 'grado_causalidad' | 'conclusiones'>): boolean =>
  Boolean(e.grado_causalidad) && Boolean((e.conclusiones ?? '').trim());

/** La guía de gravedad: el criterio vigente, el criterio preliminar del registrador (con su nombre) y el aviso. */
function GuiaDeGravedadVigente({ guia }: Readonly<{ guia: GuiaDeGravedad | null }>) {
  return (
    <Card title="Guía: criterio de gravedad vigente" icon={ClipboardList} subtitle="Es una guía, no una respuesta: nada llega preseleccionado.">
      {guia ? (
        <ul className="list-disc pl-5 text-meta space-y-1" aria-label="Criterio vigente">
          {guia.criterios.map((c) => <li key={c}>{etiquetaDelCriterio(c)}</li>)}
        </ul>
      ) : <p className="text-meta text-ink-soft">Cargando la guía…</p>}
      {guia?.criterio_preliminar_del_registrador && (
        <p className="mt-3 text-meta text-ink-soft" data-testid="criterio-del-registrador">
          Criterio del registrador: <b>{guia.criterio_preliminar_del_registrador === 'grave' ? 'grave' : 'no grave'}</b>{guia.criterio_preliminar_de ? ` (${guia.criterio_preliminar_de})` : ''} · informativo, en solo lectura
        </p>
      )}
      {guia?.aviso && <p className="mt-2 text-meta text-ink-soft">{guia.aviso}</p>}
    </Card>
  );
}

/** Los criterios en un orden fijo, para comparar lo marcado con lo guardado sin que importe el orden de las casillas. */
const ordenados = (criterios: readonly string[]): string[] => [...criterios].sort((a, b) => a.localeCompare(b));

function PestanaGravedad({ evaluacion, onCambio, actor, onSucio, onIrACausalidad }: {
  evaluacion: Evaluacion; onCambio: (e: Evaluacion) => void; actor: string;
  /** Orden del operador (18-sep-2026): la gravedad se decide después de la causalidad; el atajo a esa pestaña. */
  onIrACausalidad?: () => void;
} & AvisaCambios) {
  const alRechazar = useAlRechazar();
  const [guia, setGuia] = useState<GuiaDeGravedad | null>(null);
  // C1/C3: el campo Gravedad está VACÍO al abrir, aunque el registrador haya opinado.
  const [gravedad, setGravedad] = useState<'grave' | 'no_grave' | ''>(evaluacion.gravedad ?? '');
  const [criterios, setCriterios] = useState<string[]>(evaluacion.criterios_gravedad ?? []);
  const [sustento, setSustento] = useState('');
  const [justificacionDelCambio, setJustificacionDelCambio] = useState('');
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [ocupado, setOcupado] = useState(false);
  const [exito, setExito] = useState<string | null>(null);
  // Con la gravedad ya decidida el formulario va PLEGADO tras «Cambiar gravedad»: lo
  // normal al abrir un caso decidido es consultarla, no cambiarla (RN-17).
  const [editando, setEditando] = useState(evaluacion.gravedad === null);

  useEffect(() => {
    let vivo = true;
    guiaDeGravedad(evaluacion.caso_id)
      .then((g) => { if (vivo) setGuia(g); })
      .catch((e) => { if (vivo) setFallo(aFallo(e)); });
    return () => { vivo = false; };
  }, [evaluacion.caso_id]);

  const yaDecidida = evaluacion.gravedad !== null;
  // Sucio solo con el formulario abierto: una gravedad decidida y plegada no tiene nada pendiente.
  const criteriosGuardados = ordenados(evaluacion.criterios_gravedad ?? []).join('|');
  const sucio = editando && (
    gravedad !== (evaluacion.gravedad ?? '')
    || sustento.trim() !== ''
    || justificacionDelCambio.trim() !== ''
    || ordenados(criterios).join('|') !== criteriosGuardados
  );
  useEffect(() => { onSucio?.(sucio); }, [sucio, onSucio]);

  // La casilla de un criterio: lo mete en la lista de los que se cumplen o lo saca.
  const marcarCriterio = (criterio: string, marcado: boolean) =>
    setCriterios((cs) => (marcado ? [...cs, criterio] : cs.filter((x) => x !== criterio)));

  const guardar = async () => {
    const problemas: Record<string, string> = {};
    if (!gravedad) problemas.gravedad = 'Decide la gravedad: grave o no grave.';
    if (!sustento.trim()) problemas.sustento = 'El sustento de la decisión es obligatorio.';
    // B9 (QA 01-10-2026): `maxLength` frena lo que se escribe; esto, lo pegado de más.
    else if (sustento.trim().length > TOPE_DE_TEXTO_LIBRE_DEL_COMITE) problemas.sustento = mensajeDeTopeSuperado('El sustento', sustento.trim().length, TOPE_DE_TEXTO_LIBRE_DEL_COMITE);
    if (gravedad === 'grave' && criterios.length === 0) problemas.criterios = 'Un caso grave cita al menos un criterio.';
    // C9: cambiar una gravedad ya decidida exige justificar el cambio.
    if (yaDecidida && !justificacionDelCambio.trim()) problemas.justificacion = 'Cambiar la gravedad exige una justificación.';
    else if (yaDecidida && justificacionDelCambio.trim().length > TOPE_DE_TEXTO_LIBRE_DEL_COMITE) {
      problemas.justificacion = mensajeDeTopeSuperado('La justificación del cambio', justificacionDelCambio.trim().length, TOPE_DE_TEXTO_LIBRE_DEL_COMITE);
    }
    setErrores(problemas);
    if (Object.keys(problemas).length || !gravedad) return;
    setFallo(null);
    setOcupado(true);
    try {
      onCambio(await decidirGravedad(evaluacion.caso_id, {
        gravedad, sustento: sustento.trim(), decidida_por: actor,
        criterios: gravedad === 'grave' ? criterios : [],
        justificacion_del_cambio: yaDecidida ? justificacionDelCambio.trim() : null,
      }));
      setExito(`Gravedad decidida: ${gravedad === 'grave' ? 'grave' : 'no grave'} por ${actor}`);
      setSustento('');
      setJustificacionDelCambio('');
      setEditando(false);
    } catch (e) {
      setFallo(alRechazar(aFallo(e)));
    } finally {
      setOcupado(false);
    }
  };

  const soloLectura = !evaluacion.puede_editar;
  // Qué muestra la tarjeta: la lectura, el aviso de causalidad, el resumen plegado o el formulario.
  let modo: 'lectura' | 'sin_causalidad' | 'plegado' | 'formulario' = 'formulario';
  if (soloLectura) modo = 'lectura';
  else if (!causalidadCompleta(evaluacion)) modo = 'sin_causalidad';
  else if (!editando) modo = 'plegado';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <GuiaDeGravedadVigente guia={guia} />

      <Card title="Decidir gravedad" icon={AlertTriangle}>
        {evaluacion.gravedad && (
          <div className="mb-3 flex flex-wrap gap-2">
            <StatusBadge tone={evaluacion.gravedad === 'grave' ? 'danger' : 'neutral'}>
              {evaluacion.gravedad === 'grave' ? 'Grave' : 'No grave'}
            </StatusBadge>
            {evaluacion.ampliacion_habilitada && <StatusBadge tone="warning" icon={CheckCircle2}>Ampliar informe (act. 8) habilitado</StatusBadge>}
            {evaluacion.incluir_en_proximo_ips && <StatusBadge tone="info">Incluir en el próximo IPS</StatusBadge>}
          </div>
        )}
        {/* B7 (QA 01-10-2026): decidida grave, los criterios marcados se ven aquí y no solo en el
            informe. Con el formulario abierto los muestran las casillas. */}
        {evaluacion.gravedad === 'grave' && modo !== 'formulario' && (evaluacion.criterios_gravedad ?? []).length > 0 && (
          <div className="mb-3">
            <p id="evaluacion-gravedad-criterios-titulo" className="text-meta font-semibold text-ink-soft mb-1">Criterios que se cumplen</p>
            <ul data-testid="evaluacion-gravedad-criterios" aria-labelledby="evaluacion-gravedad-criterios-titulo" className="list-disc pl-5 text-meta space-y-1">
              {evaluacion.criterios_gravedad.map((c) => <li key={c}>{etiquetaDelCriterio(c)}</li>)}
            </ul>
          </div>
        )}
        {exito && <div className="mb-3"><Alert tone="success" title={exito} onCerrar={() => setExito(null)} /></div>}
        {fallo && <div className="mb-3"><Alert data-testid="evaluacion-error-envio" tone="danger" title="No se pudo decidir" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert></div>}
        {modo === 'lectura' && (
          <p data-testid="evaluacion-gravedad-solo-lectura" className="text-meta text-ink-soft">{evaluacion.gravedad ? 'Decidida por el Comité.' : 'El Comité todavía no ha decidido la gravedad.'}</p>
        )}
        {modo === 'sin_causalidad' && (
          // La gravedad se sustenta en la causalidad y la conclusión, y otros módulos las
          // exigen: sin ellas el formulario no se ofrece (el servidor también lo rechaza).
          <Alert tone="info" title="Primero completa «Causalidad y conclusiones»">
            La decisión de gravedad se sustenta en la causalidad y en la conclusión del Comité; guárdalas y vuelve aquí.
            {onIrACausalidad && <> <Button size="sm" variant="secondary" icon={ArrowRight} onClick={onIrACausalidad}>Ir a Causalidad y conclusiones</Button></>}
          </Alert>
        )}
        {modo === 'plegado' && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-meta text-ink-soft">Cambiarla exige una justificación y no reinicia el plazo regulatorio.</p>
            <Button data-testid="evaluacion-gravedad-cambiar" variant="secondary" icon={PenLine} onClick={() => setEditando(true)}>Cambiar gravedad</Button>
          </div>
        )}
        {modo === 'formulario' && (
          <>
            <Field label="Gravedad" required error={errores.gravedad}>
              <Select aria-label="Gravedad" placeholder="Sin decidir" value={gravedad}
                onChange={(e) => setGravedad(String(e.target.value ?? '') as 'grave' | 'no_grave' | '')}
                options={[{ value: 'grave', label: 'Grave' }, { value: 'no_grave', label: 'No grave' }]} />
            </Field>
            {gravedad === 'grave' && guia && (
              <Field label="Criterios que se cumplen" required grupo error={errores.criterios}>
                <div className="flex flex-col gap-1">
                  {guia.criterios.map((c) => (
                    <Checkbox key={c} label={etiquetaDelCriterio(c)} checked={criterios.includes(c)}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => marcarCriterio(c, e.target.checked)} />
                  ))}
                </div>
              </Field>
            )}
            <Field label="Sustento" required error={errores.sustento} contador={cuentaAtras(sustento, TOPE_DE_TEXTO_LIBRE_DEL_COMITE)}>
              <Textarea data-testid="evaluacion-gravedad-sustento" aria-label="Sustento" rows={2} value={sustento} maxLength={TOPE_DE_TEXTO_LIBRE_DEL_COMITE}
                onChange={(e) => setSustento(e.target.value)} />
            </Field>
            {yaDecidida && (
              <Field label="Justificación del cambio" required error={errores.justificacion} hint="Este caso ya tiene gravedad decidida. El plazo regulatorio se cuenta desde la recepción, no desde el cambio."
                contador={cuentaAtras(justificacionDelCambio, TOPE_DE_TEXTO_LIBRE_DEL_COMITE)}>
                <Textarea data-testid="evaluacion-gravedad-justificacion" aria-label="Justificación del cambio" rows={2} value={justificacionDelCambio} maxLength={TOPE_DE_TEXTO_LIBRE_DEL_COMITE}
                  onChange={(e) => setJustificacionDelCambio(e.target.value)} />
              </Field>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="evaluacion-gravedad-decidir" icon={Save} onClick={() => { guardar(); }} disabled={ocupado}>{yaDecidida ? 'Guardar el cambio de gravedad' : 'Decidir gravedad'}</Button>
              {yaDecidida && <Button variant="ghost" onClick={() => { setEditando(false); setErrores({}); }}>Cancelar</Button>}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

/** Un archivo elegido y todavia no subido, con su clasificacion L2 (RN-09). */
type ArchivoPendiente = { id: string; archivo: File; contienePersonas: boolean };

/**
 * Por qué NO se puede adjuntar este archivo, o `null` si cabe. El formato, el tamaño y el
 * archivo vacío los juzga `problemaDelAnexo` —la MISMA regla que usa la pestaña «Ampliar
 * informe», porque para el servidor es el mismo anexo—; aquí se añade lo que solo existe
 * en esta tarjeta: el duplicado contra la lista por subir y contra lo ya subido.
 *
 * Subir dos veces el mismo archivo es el descuido que QA reportó el 22-sep-2026.
 */
function problemaDelArchivo(
  archivo: File,
  limites: Pick<Evaluacion, 'anexo_extensiones_bloqueadas' | 'anexo_tamano_maximo_mb'>,
  pendientes: ArchivoPendiente[],
  yaSubidos: Aporte[],
  zonaHoraria: string | null,
): string | null {
  const delAnexo = problemaDelAnexo(archivo, limites);
  if (delAnexo) return delAnexo;
  if (pendientes.some((p) => nombreComparable(p.archivo.name) === nombreComparable(archivo.name))) {
    return `«${archivo.name}» ya está en la lista por subir: quítalo si quieres reemplazarlo.`;
  }
  // El duplicado contra lo YA SUBIDO (QA, 22-sep-2026): antes solo se miraba la lista
  // pendiente, así que el mismo documento entraba dos veces en el caso con solo volver a
  // elegirlo. Se compara por nombre porque es lo único que el servidor devuelve del
  // aporte; el duplicado por contenido exige que la API mande la huella.
  //
  // LO RETIRADO NO CUENTA (22-sep-2026): si lo quitaste, volver a subirlo es justo lo que
  // querías hacer, y bloquearlo dejaba al especialista sin forma de corregir su documento.
  const subido = yaSubidos.find(
    (a) => !a.retirado_en && a.archivo_nombre && nombreComparable(a.archivo_nombre) === nombreComparable(archivo.name),
  );
  if (subido) {
    const cuando = comoFechaYHora(subido.registrado_en, zonaHoraria);
    return `«${archivo.name}» ya está subido en este caso: lo subió ${subido.nombre} el ${cuando}. Si es una versión nueva, cámbiale el nombre antes de subirla.`;
  }
  return null;
}

/** Los límites vigentes, dichos en la pantalla; vacío si el servidor no los envía. */
const textoDeLosLimites = (limites: Pick<Evaluacion, 'anexo_extensiones_bloqueadas' | 'anexo_tamano_maximo_mb'>): string => {
  // DECÍA «una vez subido, el documento queda en el caso», y desde el 22-sep-2026 es FALSO:
  // el autor puede retirarlo. Dejarlo habría sido peor que no decir nada —el especialista
  // no subiría lo dudoso por creer que no hay marcha atrás—. Lo que sí sigue siendo cierto,
  // y se dice, es que retirar no borra: el registro de que aportó se conserva, y que el
  // Comité no retira lo ajeno.
  const fijo = 'Puedes elegir varios a la vez. Una vez subido, solo tú puedes retirarlo.';
  const limitesDichos = textoDeLosLimitesDelAnexo(limites);
  return limitesDichos ? `${limitesDichos}. ${fijo}` : fijo;
};

/**
 * Lo que puede llevar el nombre de una persona ademas de letras: el espacio, el guion del
 * apellido compuesto, el apostrofo y el punto de la abreviatura («M. Quispe», que es como
 * el Comite los escribe). Es la MISMA lista que el dominio del servidor
 * (`exigir_nombre_de_especialista`): si las dos se separan, el rechazo cambia segun quien
 * conteste. Lo que NO se admite es un digito o un simbolo — ese nombre acaba impreso en la
 * seccion 3 del FAR-03 que se manda a la autoridad.
 */
const SIGNOS_DE_UN_NOMBRE = new Set([' ', '-', "'", '.', '\u2019']);

const esNombreDePersona = (valor: string): boolean => {
  const limpio = valor.trim();
  if (!limpio) return false;
  const caracteres = [...limpio];
  return caracteres.some((c) => /\p{L}/u.test(c))
    && caracteres.every((c) => /\p{L}/u.test(c) || SIGNOS_DE_UN_NOMBRE.has(c));
};

const MENSAJE_NOMBRE_NO_VALIDO = 'El nombre solo admite letras, espacios, guiones, apóstrofos y el punto de una abreviatura.';
const MENSAJE_FILTRO_DEL_NOMBRE = 'En el nombre no entran números ni símbolos.';

/**
 * El contador del nombre: el formato de la casa («5 / 30 caracteres», el mismo de los ~45
 * campos con tope) y, AL LLEGAR AL TOPE, un aviso en texto. Solo con color no valdría
 * (WCAG 1.4.1). Sin fila en `parametro` no hay contador: la pantalla no inventa el número.
 */
const contadorDelNombre = (valor: string, tope?: number): ReactNode => {
  if (!tope) return undefined;
  const base = `${valor.length} / ${tope} caracteres`;
  if (valor.length < tope) return base;
  return <span className="text-amber-700 font-semibold">{base} · es el máximo</span>;
};

function PestanaEspecialistas({ evaluacion, onCambio, actor, esInvitado }: {
  evaluacion: Evaluacion; onCambio: (e: Evaluacion) => void; actor: string; esInvitado: boolean;
}) {
  const zona = useZonaDelPais()(evaluacion.pais_evento);
  const [sugeridos, setSugeridos] = useState<EspecialistaSugerido[]>([]);
  const [elegido, setElegido] = useState('');
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  // VALIDAR NI PRONTO NI TARDE (revisión de UX, 22-sep-2026). Mientras se escribe por
  // primera vez no se dice nada —acusar a medio teclear un correo es regañar por algo que
  // el usuario aún no terminó—; el error sale al salir del campo o al dejar de teclear
  // medio segundo, y a partir de ahí se recalcula en cada tecla para irse EN LA QUE LO
  // CORRIGE. Antes solo aparecía al pulsar «Invitar».
  const [tocadoElNombre, setTocadoElNombre] = useState(false);
  const [tocadoElCorreo, setTocadoElCorreo] = useState(false);
  const [intentoDeEnvio, setIntentoDeEnvio] = useState(false);
  // Lo que el filtro del nombre descartó, para decirlo. Un filtro mudo se vive como una
  // tecla que no responde; con el aviso se vive como ayuda.
  const [filtroQuitoAlgo, setFiltroQuitoAlgo] = useState(false);
  // Lo que el Comité había escrito a mano antes de elegir a alguien del directorio: al
  // vaciar el desplegable se devuelve, en vez de perderse.
  const [aManoNombre, setAManoNombre] = useState('');
  const [aManoCorreo, setAManoCorreo] = useState('');
  // Las listas de dominios de HU-31, si la pantalla vive dentro del proveedor. Sin el
  // —en una prueba de unidad— la validacion estructural del correo sigue rigiendo; lo
  // que se pierde es el dominio desechable y el «¿quisiste decir?».
  const catalogos = useCatalogosOpcional();
  const configuracionDeCorreos = catalogos?.CORREO ?? {};
  // El tope del nombre sale de `parametro`, como los de la evaluacion (HU-31). Sin la
  // fila no hay tope: la pantalla no inventa un numero.
  const topeDelNombre = topeDeTexto(evaluacion, 'especialista_nombre_caracteres');
  const revisionDelCorreo = revisarCorreo(correo, configuracionDeCorreos);
  // El correo «asentado»: lo que queda medio segundo después de la última tecla. Es lo que
  // decide si ya se puede juzgar, sin pintar rojo en cada pulsación.
  const correoAsentado = useDiferido(correo, 500);

  // LOS ERRORES SE DERIVAN DEL VALOR, no se guardan en estado. Guardados, se borraban con
  // CUALQUIER tecla —el aviso decía «ya está bien» cuando no lo estaba— y volvían al
  // pulsar «Invitar». Derivados, se van exactamente en la tecla que los corrige.
  const problemaDelNombre = ((): string | undefined => {
    const limpio = nombre.trim();
    if (!limpio) return intentoDeEnvio ? 'Escribe el nombre o elígelo del directorio.' : undefined;
    if (!esNombreDePersona(limpio)) return MENSAJE_NOMBRE_NO_VALIDO;
    // Alcanzable solo si el tope llega del servidor DESPUÉS de haber escrito: `maxLength`
    // cubre el resto. Mismo texto que el servidor, por `mensajeDeTopeSuperado`.
    if (topeDelNombre && limpio.length > topeDelNombre) {
      return mensajeDeTopeSuperado('El nombre', limpio.length, topeDelNombre);
    }
    return undefined;
  })();
  const errorDelNombre = !elegido && (tocadoElNombre || intentoDeEnvio) ? problemaDelNombre : undefined;
  // Se juzga cuando el usuario salió del campo, cuando pulsó «Invitar», o cuando dejó de
  // teclear medio segundo sobre algo que ya tiene arroba —antes de la arroba todavía no
  // hay nada que juzgar—.
  const correoJuzgable = tocadoElCorreo || intentoDeEnvio
    || (correoAsentado === correo && correo.includes('@'));
  const errorDelCorreo = !elegido && correoJuzgable ? revisionDelCorreo.error : undefined;
  const [observacion, setObservacion] = useState('');
  // VARIOS ARCHIVOS, EN LISTA (operador, 22-sep-2026). Antes era un solo `File`: elegir
  // el segundo PISABA al primero sin decir nada. Ahora se acumulan como en «Registrar
  // caso» (HU-07), cada uno con su marca L2 (RN-09) y su botón para quitarlo ANTES de
  // subirlo. El mismo archivo no entra dos veces en la lista.
  const [pendientes, setPendientes] = useState<ArchivoPendiente[]>([]);
  const [errorDelArchivo, setErrorDelArchivo] = useState<string | null>(null);
  const selectorDeDocumento = useRef<HTMLInputElement | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // BUG-902: el `texto` del servidor cuando la persona ya estaba invitada y vigente.
  const [yaInvitado, setYaInvitado] = useState<string | null>(null);
  const alRechazar = useAlRechazar();
  const [ocupado, setOcupado] = useState(false);
  const [aRetirar, setARetirar] = useState<Invitacion | null>(null);
  // HU-43 · el APORTE que se está retirando y el motivo que se escribe para él. El motivo
  // es opcional: se pide, no se exige (exigirlo convierte en interrogatorio lo que es un
  // derecho del que aportó).
  const [aporteARetirar, setAporteARetirar] = useState<Aporte | null>(null);
  const [motivoDelRetiro, setMotivoDelRetiro] = useState('');

  useEffect(() => {
    if (!evaluacion.puede_editar) return undefined;
    let vivo = true;
    sugerirEspecialistas(evaluacion.caso_id)
      .then((s) => { if (vivo) setSugeridos(s); })
      .catch(() => { if (vivo) setSugeridos([]); });
    return () => { vivo = false; };
  }, [evaluacion.caso_id, evaluacion.puede_editar]);

  const recargar = async () => onCambio(await verEvaluacion(evaluacion.caso_id));

  /** Ejecuta una acción con `ocupado` y el fallo capturado; devuelve si terminó bien. */
  const ejecutar = async (accion: () => Promise<void>): Promise<boolean> => {
    setFallo(null);
    setOcupado(true);
    try {
      await accion();
      return true;
    } catch (e) {
      setFallo(alRechazar(aFallo(e)));
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const invitar = async () => {
    setFallo(null);
    setYaInvitado(null);
    setIntentoDeEnvio(true);
    const s = sugeridos.find((x) => x.usuario_id === elegido);
    const nombreFinal = s?.nombre ?? nombre.trim();
    // El mensaje de cada campo va BAJO SU CAMPO —lo pintan `errorDelNombre` y
    // `errorDelCorreo`, que `intentoDeEnvio` acaba de destapar—, no en la alerta de la
    // cabecera de la tarjeta: con diez invitados listados, esa alerta queda a media
    // pantalla del campo que hay que arreglar. La alerta se reserva para el servidor.
    if (!nombreFinal) return;
    // El nombre escrito a mano se juzga aquí y en el servidor con la misma regla; el del
    // directorio se acepta tal cual —lo escribió Recursos Humanos, no el Comité—.
    if (!s && problemaDelNombre) return;
    // El correo se valida ANTES de enviar: el 422 del servidor llegaba como
    // «String should match pattern…», que no le dice al Comité qué corregir.
    const correoFinal = s ? (s.correo ?? '') : normalizarCorreo(correo);
    if (!s && correoFinal && revisionDelCorreo.error) return;
    await ejecutar(async () => {
      const registrada = await invitarEspecialista(evaluacion.caso_id, {
        nombre: nombreFinal, invitado_por: actor, correo: correoFinal || null, especialista_id: s?.usuario_id ?? null,
      });
      if (registrada.ya_estaba_invitado) {
        // BUG-902-C1: ya estaba invitado y vigente. El servidor no escribió nada ni mandó
        // correo, y lo dice en `texto`: se muestra tal cual, como aviso y no como éxito,
        // porque no se creó nada. Nunca el mensaje de invitación nueva, que promete un correo.
        setAviso(null);
        setYaInvitado(registrada.texto ?? null);
      } else {
        // SIN CORREO NO HAY AVISO, y prometerlo es mentir sobre algo ya consumado: el campo
        // es opcional y el dominio acepta la invitación sin él (revisión de UX, 22-sep-2026).
        setAviso(correoFinal
          ? `${nombreFinal} recibe un correo con el enlace al caso; su acceso es de solo lectura y solo a este caso.`
          : `${nombreFinal} queda invitado, pero no recibió aviso porque no tiene correo. Pásale el enlace o invítalo de nuevo con su correo.`);
      }
      setElegido(''); setNombre(''); setCorreo('');
      setTocadoElNombre(false); setTocadoElCorreo(false); setIntentoDeEnvio(false); setFiltroQuitoAlgo(false);
      await recargar();
    });
  };

  const retirar = async (invitacion: Invitacion) => {
    setARetirar(null);
    await ejecutar(async () => { await retirarEspecialista(evaluacion.caso_id, invitacion.id); await recargar(); });
  };

  const participacion = async (invitacionId: string, participo: boolean) => {
    await ejecutar(async () => { await marcarParticipacion(evaluacion.caso_id, invitacionId, participo); await recargar(); });
  };

  const observar = async () => {
    if (!observacion.trim()) return;
    await ejecutar(async () => { await observarComoEspecialista(evaluacion.caso_id, observacion.trim()); setObservacion(''); await recargar(); });
  };

  /**
   * Añade el archivo a la lista, PRE-VALIDANDO con los límites vigentes de `parametro`
   * (los mismos de HU-07, que ya llegan con la evaluación). La validación de verdad es
   * del servidor; esto ahorra el viaje y el 422 cuando ya se sabe que no cabe.
   */
  const agregarArchivo = (elegido: File | null) => {
    if (!elegido) return;
    const problema = problemaDelArchivo(elegido, evaluacion, pendientes, evaluacion.aportes_de_especialistas ?? [], zona);
    if (problema) { setErrorDelArchivo(problema); return; }
    setErrorDelArchivo(null);
    setPendientes((ps) => [...ps, { id: crypto.randomUUID(), archivo: elegido, contienePersonas: false }]);
  };

  /** Abre el selector oculto. El `<input>` del sistema operativo no se pinta: su botón
   *  «Seleccionar archivo» convivía con el nuestro y nadie sabía cuál consumaba la acción. */
  const elegirDocumentos = () => selectorDeDocumento.current?.click();

  const quitarPendiente = (id: string) => setPendientes((ps) => ps.filter((p) => p.id !== id));
  const marcarL2 = (id: string, valor: boolean) =>
    setPendientes((ps) => ps.map((p) => (p.id === id ? { ...p, contienePersonas: valor } : p)));

  const adjuntar = async () => {
    if (pendientes.length === 0) return;
    setFallo(null);
    setErrorDelArchivo(null);
    setOcupado(true);
    const subidos: string[] = [];
    try {
      // En serie y no en paralelo: el orden de llegada tiene que ser el mismo que el de
      // la lista que el usuario ve, y el servidor cuenta el presupuesto del caso.
      for (const p of pendientes) {
        await adjuntarComoEspecialista(evaluacion.caso_id, p.archivo.name, await archivoABase64(p.archivo), p.contienePersonas);
        subidos.push(p.id);
      }
      setAviso(pendientes.length > 1
        ? `Se subieron ${pendientes.length} documentos.`
        : 'Documento subido.');
    } catch (e) {
      // SOLO se quita de la lista lo que SÍ entró (QA, 22-sep-2026): al reintentar una
      // tanda que falló a la mitad, lo ya subido se subía por segunda vez.
      const fallo = aFallo(e);
      const quedan = pendientes.length - subidos.length;
      setFallo(subidos.length > 0
        ? { ...fallo, message: `Se subieron ${subidos.length} de ${pendientes.length} documentos. ${fallo.message} Los ${quedan} que faltan siguen en la lista: corrige y vuelve a intentarlo.` }
        : fallo);
    } finally {
      setPendientes((ps) => ps.filter((p) => !subidos.includes(p.id)));
      setOcupado(false);
      await recargar();
    }
  };

  const descargar = async (a: Aporte) => {
    setFallo(null);
    try {
      descargarBlob(await descargarAporte(evaluacion.caso_id, a.id), a.archivo_nombre ?? 'aporte');
    } catch (e) { setFallo(aFallo(e)); }
  };

  const cerrarElRetiroDelAporte = () => { setAporteARetirar(null); setMotivoDelRetiro(''); };

  /**
   * RETIRAR UN APORTE PROPIO (22-sep-2026). El especialista podía quitar lo que aún no
   * había subido, pero no lo ya subido: un documento equivocado se quedaba en el caso para
   * siempre. No borra nada — el aporte deja de estar a disposición del Comité y queda el
   * registro de que se hizo y de que se retiró, con la fecha.
   */
  const retirarElAporte = async (a: Aporte) => {
    const motivo = motivoDelRetiro.trim();
    cerrarElRetiroDelAporte();
    await ejecutar(async () => { await retirarAporte(evaluacion.caso_id, a.id, motivo || null); await recargar(); });
  };

  // El Comité puede cortar el acceso aunque la evaluación esté congelada; al invitado y
  // al aprobador la vista ya les pone todos los permisos en falso.
  const puedeRetirar = evaluacion.puede_retirar_especialistas ?? evaluacion.puede_editar;

  const opciones = sugeridos.map((s) => ({
    value: s.usuario_id,
    label: `${s.nombre}${s.del_pais_del_evento ? ` · ${evaluacion.pais_evento}` : s.paises.length ? ` · ${s.paises.join(', ')}` : ''}`,
  }));

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Card title="Especialistas invitados" icon={Users} subtitle="Acceso de solo lectura y solo a este caso. El registro se conserva al retirar.">
        {fallo && <div className="mb-3"><Alert data-testid="evaluacion-especialistas-error-envio" tone="danger" title="No se pudo completar" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert></div>}
        {aviso && <div className="mb-3"><Alert data-testid="evaluacion-especialistas-exito" tone="success" title={aviso} onCerrar={() => setAviso(null)} /></div>}
        {yaInvitado && <div className="mb-3"><Alert data-testid="evaluacion-especialistas-ya-invitado" tone="info" title={yaInvitado} onCerrar={() => setYaInvitado(null)} /></div>}
        {evaluacion.invitaciones.length === 0
          ? <p data-testid="evaluacion-especialistas-vacio" className="text-meta text-ink-soft">Todavía no hay especialistas invitados.</p>
          : (
            <ul className="divide-y divide-hairline" aria-label="Invitaciones">
              {evaluacion.invitaciones.map((i) => (
                <li key={i.id} data-testid="evaluacion-especialistas-fila" className="py-2 flex flex-wrap items-center gap-2 text-meta">
                  <span className="font-medium">{i.nombre}</span>
                  {i.correo && <span className="inline-flex items-center gap-1 text-ink-soft"><Mail size={12} aria-hidden="true" /> {i.correo}</span>}
                  <span className="text-ink-soft">invitado por {i.invitado_por} · {fecha(i.invitado_en, zona)}</span>
                  {i.vigente
                    ? <StatusBadge tone="success">Vigente</StatusBadge>
                    : <StatusBadge tone="neutral">Retirada · {fecha(i.retirado_en, zona)}</StatusBadge>}
                  <span className="ml-auto flex items-center gap-2">
                    {/* «Participó en el análisis» alimenta la sección 3 del FAR-03, así que
                        se congela mientras el aprobador lee el informe (C4). RETIRAR no: es
                        cortar el acceso de un tercero, y ni HU-43-C4 ni el endpoint lo atan
                        al estado del caso. Por eso cuelga de su propio permiso. */}
                    {evaluacion.puede_editar && (
                      <Checkbox data-testid="evaluacion-especialistas-fila-participo" label="Participó en el análisis" checked={i.participo_en_el_analisis}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => { participacion(i.id, e.target.checked); }} />
                    )}
                    {i.vigente && puedeRetirar && (
                      <Button data-testid="evaluacion-especialistas-fila-retirar" size="sm" variant="secondary" icon={UserMinus} onClick={() => setARetirar(i)} disabled={ocupado} aria-label={`Retirar a ${i.nombre}`}>Retirar</Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        {evaluacion.puede_editar && (
          <div className="mt-4 border-t border-hairline pt-3">
            <p className="text-meta font-semibold mb-2 flex items-center gap-1.5"><UserPlus size={14} /> Invitar especialista</p>
            <Field label="Personal sugerido" hint={`Primero el personal de ${evaluacion.pais_evento ?? 'el país del evento'}.`}>
              {/* Al elegir del directorio se COPIAN sus datos a los dos campos y se apagan;
                  al vaciar el desplegable vuelve lo que el Comité había tecleado. Antes
                  solo se apagaban: quedaba «Juan Pérez» en gris y se invitaba a María
                  Quispe — lo que se veía no era lo que el sistema iba a hacer. Mismo patrón
                  que «los datos del dueño son los mismos» de Registrar caso. */}
              <Select aria-label="Personal sugerido" placeholder="Elegir del directorio…" value={elegido}
                onChange={(e) => {
                  const id = String(e.target.value ?? '');
                  setElegido(id);
                  const s = sugeridos.find((x) => x.usuario_id === id);
                  if (s) {
                    setAManoNombre(nombre); setAManoCorreo(correo);
                    setNombre(s.nombre); setCorreo(s.correo ?? '');
                  } else {
                    setNombre(aManoNombre); setCorreo(aManoCorreo);
                  }
                  setFiltroQuitoAlgo(false);
                }} options={opciones} />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Sin `aria-label`: `Field` ya pinta un <label htmlFor> y un `aria-label` lo
                  PISA, así que el lector de pantalla anunciaba «Nombre del especialista»
                  donde la etiqueta visible dice otra cosa (WCAG 2.5.3, Label in Name). */}
              <Field label="O escribe su nombre" error={errorDelNombre}
                hint={elegido ? 'Viene del directorio. Vacía «Personal sugerido» para escribirlo a mano.'
                  : (filtroQuitoAlgo ? MENSAJE_FILTRO_DEL_NOMBRE : 'Solo letras: sin números ni símbolos.')}
                contador={elegido ? undefined : contadorDelNombre(nombre, topeDelNombre)}>
                <Input value={nombre} maxLength={topeDelNombre} disabled={!!elegido}
                  onChange={(e) => {
                    // Se filtra en el `onChange` y no con el teclado: bloquear teclas deja
                    // pasar el PEGADO, que es por donde llega la mitad de la basura.
                    const limpio = soloNombreDePersona(e.target.value, topeDelNombre);
                    setFiltroQuitoAlgo(limpio !== e.target.value);
                    setNombre(limpio);
                  }}
                  onBlur={() => setTocadoElNombre(true)} />
              </Field>
              {/* La sugerencia («¿quisiste decir @gmail.com?») NO bloquea el envío: un
                  dominio corporativo legítimo puede parecerse a uno frecuente. Igual que
                  en «Registrar caso» — de donde viene también `pieDeDosLineas`: esa
                  sugerencia aparece y desaparece mientras se teclea y a media anchura
                  ocupa dos líneas, así que sin reservar el carril la fila crece y encoge
                  13 px bajo el cursor (medido el 9-sep-2026, ver `ui.tsx`). */}
              <Field label="Correo" error={errorDelCorreo} pieDeDosLineas
                hint={elegido ? 'Viene del directorio.'
                  : (revisionDelCorreo.sugerencia ?? 'Opcional. Es a donde llega el enlace al caso.')}>
                <Input type="email" inputMode="email" value={correo} disabled={!!elegido}
                  onChange={(e) => setCorreo(soloCorreo(e.target.value))}
                  onBlur={() => { setCorreo(normalizarCorreo(correo)); setTocadoElCorreo(true); }} />
              </Field>
            </div>
            <Button data-testid="evaluacion-especialistas-invitar" icon={UserPlus} onClick={() => { invitar(); }} disabled={ocupado} aria-busy={ocupado}>
              {ocupado ? 'Invitando…' : 'Invitar'}
            </Button>
          </div>
        )}
        <Modal open={aRetirar !== null} onClose={() => setARetirar(null)} tone="warning" icon={UserMinus}
          eyebrow="Comité · Especialistas" title={`¿Retirar a ${aRetirar?.nombre ?? 'este especialista'}?`}
          footer={<>
            <Button variant="secondary" onClick={() => setARetirar(null)}>Cancelar</Button>
            <Button variant="danger" icon={UserMinus} onClick={() => { if (aRetirar) retirar(aRetirar); }}>Retirar la invitación</Button>
          </>}>
          <p>Pierde el acceso al caso desde ahora. La invitación queda registrada como retirada.</p>
        </Modal>
      </Card>

      <Card title="Aportes de los especialistas" icon={MessageSquarePlus}
        subtitle="Observaciones y documentos de los especialistas invitados, para consultar al elaborar el FAR-03.">
        {evaluacion.aportes_de_especialistas.length === 0
          ? <p className="text-meta text-ink-soft">Sin aportes todavía.</p>
          : (
            <ul className="divide-y divide-hairline" aria-label="Aportes">
              {evaluacion.aportes_de_especialistas.map((a) => {
                // Un aporte RETIRADO no se pinta a medias: ni su texto, ni su descarga, ni
                // su marca L2 — el servidor ni siquiera manda el nombre del archivo. Lo que
                // queda es quién aportó, cuándo, y que lo retiró.
                const retirado = Boolean(a.retirado_en);
                return (
                  <li key={a.id} className="py-2 text-meta">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{a.nombre}</span>
                      <span className="text-ink-soft">{comoFechaYHora(a.registrado_en, zona)}</span>
                      {retirado && <StatusBadge tone="neutral">Retirado · {fecha(a.retirado_en, zona)}</StatusBadge>}
                      {!retirado && a.tipo === 'documento' && (
                        <Button size="sm" variant="secondary" icon={Download} onClick={() => { descargar(a); }}>{a.archivo_nombre}</Button>
                      )}
                      {!retirado && a.contiene_dato_de_personas && <L2Tag>Datos de personas · L2</L2Tag>}
                      {/* Solo el AUTOR: el permiso lo decide el servidor y el Comité no lo
                          tiene. Con borde y no fantasma, que va dentro de una lista. */}
                      {a.puede_retirarlo && !retirado && (
                        <Button size="sm" variant="secondary" icon={Trash2} className="ml-auto" disabled={ocupado}
                          aria-label={`Retirar mi aporte del ${fecha(a.registrado_en, zona)}`}
                          onClick={() => { setMotivoDelRetiro(''); setAporteARetirar(a); }}>Quitar</Button>
                      )}
                    </div>
                    {!retirado && a.texto && <p className="mt-1">{a.texto}</p>}
                    {retirado && a.motivo_del_retiro && <p className="mt-1 text-ink-soft">Motivo: {a.motivo_del_retiro}</p>}
                  </li>
                );
              })}
            </ul>
          )}
        {esInvitado && (
          <div className="mt-4 border-t border-hairline pt-3 space-y-3">
            <Field label="Agregar observación">
              <Textarea aria-label="Observación del especialista" rows={2} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
            </Field>
            <Button size="sm" icon={MessageSquarePlus} onClick={() => { observar(); }} disabled={ocupado || !observacion.trim()}>Agregar observación</Button>
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-compacto font-semibold text-ink">Documentos de sustento</p>
                <Button variant="secondary" size="sm" icon={Paperclip} onClick={elegirDocumentos} disabled={ocupado}>
                  Elegir documentos
                </Button>
              </div>
              {/* El selector real va OCULTO y lo dispara el botón de arriba: un control del
                  sistema operativo junto a uno de la casa duplicaba el verbo —«Seleccionar
                  archivo» y «Adjuntar documento»— y nadie sabía cuál consumaba la acción
                  (revisión de UX, 22-sep-2026). Mismo patrón que «Registrar caso».
                  `accept` sin lista blanca: la regla es la lista negra de `parametro`. */}
              <input ref={selectorDeDocumento} type="file" className="hidden" aria-hidden="true" tabIndex={-1}
                onChange={(e) => {
                  agregarArchivo(e.target.files?.[0] ?? null);
                  // Se limpia para poder volver a elegir el MISMO archivo: sin esto el
                  // navegador no dispara `change` la segunda vez y la pantalla parece colgada.
                  e.target.value = '';
                }} />
              <p className="text-meta text-ink-soft">{textoDeLosLimites(evaluacion)}</p>
              {errorDelArchivo && (
                <div className="mt-2"><Alert tone="warning" title="No se pudo agregar" onCerrar={() => setErrorDelArchivo(null)}>{errorDelArchivo}</Alert></div>
              )}
            </div>
            {pendientes.length > 0 && (
              <>
                <p className="text-compacto font-semibold text-ink">Por subir ({pendientes.length})</p>
                <ul className="space-y-1.5" aria-label="Documentos por subir">
                  {pendientes.map((p) => (
                    <li key={p.id} className="px-3 py-2 bg-canvas border border-hairline rounded-xl space-y-1">
                      <p className="text-meta text-ink break-all" title={p.archivo.name}>
                        <Paperclip size={13} className="inline text-ink-soft mr-1" aria-hidden="true" />{p.archivo.name}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-meta text-ink-soft whitespace-nowrap">{tamanoLegible(p.archivo.size)}</span>
                        <Button size="sm" variant="secondary" icon={Trash2} disabled={ocupado}
                          aria-label={`Quitar «${p.archivo.name}» de la lista por subir`}
                          onClick={() => quitarPendiente(p.id)}>Quitar</Button>
                      </div>
                      <Checkbox label="Contiene datos de personas" checked={p.contienePersonas} disabled={ocupado}
                        hint="Nombres, diagnósticos o cualquier dato de salud de una persona. Se guarda como L2 y solo lo abre quien tiene ese permiso."
                        onChange={(e: ChangeEvent<HTMLInputElement>) => marcarL2(p.id, e.target.checked)} />
                    </li>
                  ))}
                </ul>
                <Button size="sm" icon={Paperclip} onClick={() => { adjuntar(); }} disabled={ocupado}>
                  {pendientes.length > 1 ? `Subir ${pendientes.length} documentos` : 'Subir el documento'}</Button>
              </>
            )}
          </div>
        )}
        {/* Mismo cuadro que «¿Retirar a este especialista?»: confirmar y, si se quiere,
            decir por qué. El texto no promete un borrado que no ocurre. */}
        <Modal open={aporteARetirar !== null} onClose={cerrarElRetiroDelAporte} tone="warning" icon={Trash2}
          eyebrow="Especialista · Aportes" title="¿Retirar tu aporte?"
          footer={<>
            <Button variant="secondary" onClick={cerrarElRetiroDelAporte}>Cancelar</Button>
            <Button variant="danger" icon={Trash2} onClick={() => { if (aporteARetirar) retirarElAporte(aporteARetirar); }}>Retirar el aporte</Button>
          </>}>
          <p>El aporte deja de estar disponible para el Comité. Queda registrado que lo retiraste.</p>
          <Field label="Motivo" hint="Opcional. Ayuda al Comité a entender por qué ya no cuenta con él.">
            <Textarea aria-label="Motivo del retiro" rows={2} value={motivoDelRetiro}
              onChange={(e) => setMotivoDelRetiro(e.target.value)} />
          </Field>
        </Modal>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HU-13 · Acciones inmediatas y preventivas
// ---------------------------------------------------------------------------

/** HU-13-C5: la opción que abre el texto libre. No es una clase del catálogo. */
const OTRA_ACCION = '__otra__';
// Bug 716 (ADO, HU-13-C5): el catálogo `clase_accion` trae su propia «Otra» (`otra`), que
// convivía con el centinela de arriba: elegirla se guardaba como clase `otra` sin
// descripción y sin abrir el texto libre. Esas entradas se quedan FUERA del desplegable;
// la única vía libre es «Otra acción (escribir)». `nombreDeLaClase` las sigue resolviendo
// para las acciones ya guardadas con esa clase.
const CODIGOS_DE_OTRA_DEL_CATALOGO = new Set(['otra', 'otro']);
const esOtraDelCatalogo = (codigo: string): boolean => CODIGOS_DE_OTRA_DEL_CATALOGO.has(codigo.trim().toLowerCase());
const TITULO_DE_SECCION: Record<number, string> = { 7: 'Solución inmediata', 8: 'Solución preventiva' };
export const MENSAJE_RESPONSABLE = 'El responsable es obligatorio';
export const MENSAJE_PLAZO = 'El plazo es obligatorio';
const MENSAJE_QUE = 'Elige una acción del catálogo o escribe la tuya';

type OpcionDeCatalogo = { codigo: string; nombre?: string; activo?: boolean };
type ErroresDeLaAccion = { tipo?: string; que?: string; responsable?: string; plazo?: string; observacion?: string };

function PestanaAcciones({ evaluacion, onCambio, onSucio }: {
  evaluacion: Evaluacion; onCambio: (e: Evaluacion) => void;
} & AvisaCambios) {
  const zona = useZonaDelPais()(evaluacion.pais_evento);
  const soloLectura = !evaluacion.puede_editar;
  // C2 (21-sep-2026): el ESTADO de un compromiso se sigue hasta el cierre (C-08), aunque el
  // caso ya no esté en manos del Comité; crear o quitar acciones sigue exigiendo editar.
  const puedeSeguirEstado = evaluacion.puede_seguir_acciones ?? evaluacion.puede_editar;
  const [clases, setClases] = useState<OpcionDeCatalogo[]>([]);
  const [responsables, setResponsables] = useState<OpcionDeCatalogo[]>([]);
  const [tipo, setTipo] = useState('');
  const [que, setQue] = useState('');
  const [otra, setOtra] = useState('');
  const [responsable, setResponsable] = useState('');
  const [plazo, setPlazo] = useState('');
  const [observacion, setObservacion] = useState('');
  const [errores, setErrores] = useState<ErroresDeLaAccion>({});
  const alRechazar = useAlRechazar();
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // La acción cuyo PATCH está en vuelo. El desplegable se pinta con el valor del
  // servidor, así que sin esto volvía al estado anterior mientras la petición viajaba y
  // dos cambios seguidos se pisaban entre sí (auditoría, 22-sep-2026).
  const [enVuelo, setEnVuelo] = useState<string | null>(null);
  const [aQuitar, setAQuitar] = useState<Accion | null>(null);

  // C4: el catálogo se lee al abrir la pestaña; una entrada nueva aparece sin redespliegue.
  useEffect(() => {
    let vivo = true;
    listarCatalogo('clase_accion').then((r) => { if (vivo) setClases(r.valores.filter((v) => v.activo !== false)); }).catch(() => {});
    listarCatalogo('responsable_accion').then((r) => { if (vivo) setResponsables(r.valores.filter((v) => v.activo !== false)); }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  const opcionesDeQue = useMemo(() => [
    ...clases.filter((c) => !esOtraDelCatalogo(c.codigo)).map((c) => ({ value: c.codigo, label: c.nombre ?? c.codigo })),
    { value: OTRA_ACCION, label: 'Otra acción (escribir)' },
  ], [clases]);
  const opcionesDeResponsable = useMemo(() => responsables.map((r) => ({ value: r.codigo, label: r.nombre ?? r.codigo })), [responsables]);
  const nombreDelResponsable = (codigo: string) => responsables.find((r) => r.codigo === codigo)?.nombre ?? codigo;
  const nombreDeLaClase = (codigo: string) => clases.find((c) => c.codigo === codigo)?.nombre ?? codigo;

  // Cambios sin guardar: lo mismo que Investigación, Causalidad y Gravedad hacen desde el
  // 18-sep. Sin esto, una acción a medio escribir se perdía al cambiar de pestaña.
  const sucio = Boolean(tipo || que || otra.trim() || responsable || plazo || observacion.trim());
  useEffect(() => { onSucio?.(sucio); }, [sucio, onSucio]);

  // Bug 716 (ADO, HU-13-C7): el mensaje bajo un campo se va en cuanto se corrige ESE campo;
  // los de los demás siguen hasta que se corrigen o se vuelve a guardar. Antes `errores` solo
  // se recalculaba al pulsar «Guardar acción» y el mensaje se quedaba pegado tras corregir.
  const limpiarError = (campo: keyof ErroresDeLaAccion) =>
    setErrores((e) => (e[campo] === undefined ? e : { ...e, [campo]: undefined }));

  // C7 / C7b: sin responsable o sin plazo el guardado NO ocurre y el mensaje va bajo el campo.
  const validar = () => {
    const e: ErroresDeLaAccion = {};
    if (!tipo) e.tipo = 'El tipo es obligatorio';
    if (!que || (que === OTRA_ACCION && !otra.trim())) e.que = MENSAJE_QUE;
    if (!responsable.trim()) e.responsable = MENSAJE_RESPONSABLE;
    if (!plazo) e.plazo = MENSAJE_PLAZO;
    // B9 (QA 01-10-2026): el tope del esquema; `maxLength` no frena un texto pegado de más.
    if (observacion.trim().length > TOPE_DE_TEXTO_LIBRE_DEL_COMITE) {
      e.observacion = mensajeDeTopeSuperado('El texto de «Observaciones»', observacion.trim().length, TOPE_DE_TEXTO_LIBRE_DEL_COMITE);
    }
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const guardar = async () => {
    setFallo(null);
    if (!validar()) return;
    setOcupado(true);
    try {
      onCambio(await agregarAccion(evaluacion.caso_id, {
        tipo,
        responsable,
        plazo,
        clase: que === OTRA_ACCION ? null : que,
        descripcion: que === OTRA_ACCION ? otra.trim() : null,
        observacion: observacion.trim() || null,
      }));
      setTipo(''); setQue(''); setOtra(''); setResponsable(''); setPlazo(''); setObservacion(''); setErrores({});
      setAviso(`Acción registrada en «${TITULO_DE_SECCION[tipo === 'inmediata' ? 7 : 8]}».`);
    } catch (e) {
      setFallo(alRechazar(aFallo(e)));
    } finally {
      setOcupado(false);
    }
  };

  const cambiarEstado = async (accion: Accion, estado: string) => {
    if (!estado || estado === accion.estado || enVuelo) return;
    setFallo(null);
    setAviso(null);
    setEnVuelo(accion.id);
    try { onCambio(await cambiarEstadoDeAccion(evaluacion.caso_id, accion.id, estado)); }
    catch (e) { setFallo(aFallo(e)); }
    finally { setEnVuelo(null); }
  };
  const quitar = async (accion: Accion) => {
    setAQuitar(null);
    setFallo(null);
    setEnVuelo(accion.id);
    try {
      onCambio(await quitarAccion(evaluacion.caso_id, accion.id));
      setAviso(`Se quitó «${nombreDeLaClase(accion.que_se_hace)}».`);
    } catch (e) { setFallo(alRechazar(aFallo(e))); }
    finally { setEnVuelo(null); }
  };

  const secciones = [7, 8].map((n) => ({ n, titulo: TITULO_DE_SECCION[n], acciones: evaluacion.acciones.filter((a) => a.seccion_del_informe === n) }));
  const etiquetaDelTipo = (t: string) => TIPOS_DE_ACCION.find((x) => x.value === t)?.label ?? t;
  const etiquetaDelEstado = (e: string) => ESTADOS_DE_ACCION.find((x) => x.value === e)?.label ?? e;
  const tonoDelEstado = (e: string): 'success' | 'info' | 'neutral' => (e === 'cumplida' ? 'success' : e === 'en_curso' ? 'info' : 'neutral');

  return (
    <div className="space-y-4">
      {fallo && <Alert data-testid="evaluacion-error-envio" tone="danger" title="No se pudo guardar la acción" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
      {/* La acción guardada cae en una tarjeta que suele quedar fuera de pantalla, así que sin
          este acuse el botón parecía no hacer nada (auditoría del 22-sep-2026). */}
      {aviso && <Alert tone="success" title={aviso} onCerrar={() => setAviso(null)} />}

      <Modal open={aQuitar !== null} onClose={() => setAQuitar(null)} tone="warning" icon={Trash2}
        eyebrow="Comité · Acciones" title={`¿Quitar «${aQuitar ? nombreDeLaClase(aQuitar.que_se_hace) : 'esta acción'}»?`}
        footer={<>
          <Button variant="secondary" onClick={() => setAQuitar(null)}>Cancelar</Button>
          <Button variant="danger" icon={Trash2} onClick={() => { if (aQuitar) quitar(aQuitar); }}>Quitar la acción</Button>
        </>}>
        <p>Deja de contar como compromiso del caso y el cierre ya no la exigirá.</p>
      </Modal>

      {!soloLectura && (
        <Card title="Nueva acción" icon={ListChecks}
          subtitle="Qué se hace, quién responde y para cuándo. La lista es abierta: si la acción no está en el catálogo, escríbela en «Otra acción».">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Tipo" required error={errores.tipo}>
              <Select aria-label="Tipo de acción" placeholder="Elegir el tipo…" value={tipo}
                onChange={(e) => { setTipo(String(e.target.value ?? '')); limpiarError('tipo'); }} options={[...TIPOS_DE_ACCION]} />
            </Field>
            <Field label="Qué se hace" required error={errores.que}>
              <Select aria-label="Qué se hace" placeholder="Elegir del catálogo…" value={que}
                onChange={(e) => { setQue(String(e.target.value ?? '')); limpiarError('que'); }} options={opcionesDeQue} />
            </Field>
            {que === OTRA_ACCION && (
              <Field label="Otra acción" required className="md:col-span-2">
                {/* El texto libre se valida como parte de «Qué se hace» (MENSAJE_QUE): su error es el de `que`. */}
                <Input aria-label="Otra acción" value={otra} onChange={(e) => { setOtra(e.target.value); limpiarError('que'); }} placeholder="Describe la acción" />
              </Field>
            )}
            <Field label="Responsable" required error={errores.responsable}>
              <Select aria-label="Responsable" placeholder="Elegir el responsable…" value={responsable}
                onChange={(e) => { setResponsable(String(e.target.value ?? '')); limpiarError('responsable'); }} options={opcionesDeResponsable} />
            </Field>
            <Field label="Plazo" required error={errores.plazo}>
              <Input aria-label="Plazo" type="date" value={plazo} onChange={(e) => { setPlazo(e.target.value); limpiarError('plazo'); }} />
            </Field>
            <Field label="Observaciones" hint="Opcional." className="md:col-span-2" error={errores.observacion}
              contador={cuentaAtras(observacion, TOPE_DE_TEXTO_LIBRE_DEL_COMITE)}>
              <Textarea data-testid="evaluacion-acciones-observacion" aria-label="Observaciones de la acción" rows={2} value={observacion} maxLength={TOPE_DE_TEXTO_LIBRE_DEL_COMITE}
                onChange={(e) => { setObservacion(e.target.value); limpiarError('observacion'); }} />
            </Field>
          </div>
          <div className="mt-3">
            <Button data-testid="evaluacion-acciones-guardar" icon={Save} onClick={() => { guardar(); }} disabled={ocupado}>Guardar acción</Button>
          </div>
        </Card>
      )}

      {secciones.map((s) => (
        <Card key={s.n} title={`${s.n}. ${s.titulo}`} icon={ListChecks}
          subtitle={s.n === 7 ? 'Las acciones inmediatas. Es la sección 7 del FAR-03.' : 'Las preventivas y correctivas. Es la sección 8 del FAR-03.'}>
          {s.acciones.length === 0
            ? <p className="text-meta text-ink-soft">Sin acciones en esta sección.</p>
            : (
              <ul className="divide-y divide-hairline" aria-label={s.titulo}>
                {s.acciones.map((a) => (
                  <li key={a.id} className="py-2 text-meta flex flex-wrap items-center gap-2">
                    <StatusBadge tone="info">{etiquetaDelTipo(a.tipo)}</StatusBadge>
                    <span className="font-medium min-w-0 flex-1">{nombreDeLaClase(a.que_se_hace)}</span>
                    <span className="text-ink-soft">{nombreDelResponsable(a.responsable)}</span>
                    <Mono className="text-meta">{fecha(a.plazo, zona)}</Mono>
                    {puedeSeguirEstado
                      ? (
                        /* `sinVacio`: una acción siempre tiene estado; «ninguno» no es una opción. */
                        <Select data-testid="evaluacion-acciones-fila-estado" aria-label={`Estado de ${nombreDeLaClase(a.que_se_hace)}`} compacto sinVacio value={a.estado}
                          disabled={enVuelo === a.id}
                          onChange={(e) => { cambiarEstado(a, String(e.target.value ?? '')); }} options={[...ESTADOS_DE_ACCION]} />
                      )
                      : <StatusBadge tone={tonoDelEstado(a.estado)}>{etiquetaDelEstado(a.estado)}</StatusBadge>}
                    {!soloLectura && (
                      /* Con borde y con confirmación: quitar un compromiso es irreversible y queda
                         en la auditoría. Retirar a un especialista —menos grave— ya preguntaba y
                         esto no (auditoría del 22-sep-2026). */
                      <Button data-testid="evaluacion-acciones-fila-quitar" size="sm" variant="secondary" icon={Trash2} disabled={enVuelo === a.id}
                        aria-label={`Quitar ${nombreDeLaClase(a.que_se_hace)}`} onClick={() => setAQuitar(a)}>Quitar</Button>
                    )}
                    {a.observacion && <span className="basis-full text-meta text-ink-soft pl-1">{a.observacion}</span>}
                  </li>
                ))}
              </ul>
            )}
        </Card>
      ))}
    </div>
  );
}

// HU-49-C6: la actividad 8 es solo para los graves; la pestaña no se muestra en un no grave.
const PESTANA_AMPLIACION: Pestana = { key: 'ampliacion', label: 'Ampliar informe (act. 8)', icon: FileUp };

export type PestanaDeLaEvaluacion = 'investigacion' | 'causalidad' | 'gravedad' | 'acciones' | 'especialistas' | 'informe' | 'ampliacion';

// Los estados del caso en los que el informe puede venir observado por el aprobador.
const ESTADOS_CON_INFORME_EN_REVISION = new Set(['observado', 'en_aprobacion']);

/**
 * HU-11-C5: la observación del aprobador que sigue sin subsanar (la más reciente), solo
 * mientras el caso está observado o en aprobación. En cualquier otro estado, ninguna.
 */
const observacionVigenteDe = (informe: Informe | null, estadoDelCaso: string | null): Informe['observaciones'][number] | null => {
  if (!informe || !ESTADOS_CON_INFORME_EN_REVISION.has(estadoDelCaso ?? '')) return null;
  return [...informe.observaciones].reverse().find((o) => !o.subsanado_en) ?? null;
};

/** La evaluación como la ve esta sesión: el invitado y el aprobador la leen, aunque el servidor permitiera editar. */
/**
 * La evaluación tal como la ve quien SOLO LEE (el invitado, el aprobador).
 *
 * ⚠ AQUÍ SE APAGAN TODOS LOS PERMISOS DE ESCRITURA, Y «TODOS» ES LITERAL. Faltaba
 * `puede_retirar_especialistas` y el invitado veía el botón «Retirar» sobre los demás
 * invitados (QA, 25-sep-2026). No podía ejecutarlo —el endpoint es `SOLO_COMITE` y
 * responde 403— pero ofrecerle una acción que va a fallar es un defecto igual.
 *
 * SE VE EN DESARROLLO Y NO EN PRODUCCIÓN, y por eso pasó desapercibido: el actor
 * simulado llega al backend con TODOS los roles conocidos, `comite` incluido, así que
 * el servidor manda los permisos en true sin mirar qué rol eligió la pantalla de
 * entrada. Quien distingue el rol es el front, y esta función es esa distinción.
 *
 * LA REGLA: esta máscara no consulta al servidor, lo CORRIGE. Un permiso nuevo que no
 * se apague aquí reaparece como un botón que el invitado no debería tener — y la HU es
 * tajante (RN-31): «No puede editar el caso ni la evaluación: su aporte es un aporte,
 * la decisión sigue siendo del Comité».
 */
const vistaPara = (evaluacion: Evaluacion, soloLee: boolean): Evaluacion =>
  (soloLee
    ? {
      ...evaluacion,
      puede_editar: false,
      puede_ampliar: false,
      puede_seguir_acciones: false,
      puede_retirar_especialistas: false,
    }
    : evaluacion);

/** Por qué la evaluación está en solo lectura para el Comité (C1/C2, HU-14-C9), con lo que sigue abierto. */
//
// LO QUE SIGUE ABIERTO SE DICE, TAMBIÉN CON EL INFORME EN APROBACIÓN (O-04, 28-sep-2026).
// QA vio «Retirar» en Especialistas con el FAR-03 en aprobación y lo leyó como defecto:
// el aviso enumeraba lo congelado y callaba lo abierto. Retirar a un invitado es
// intencional —HU-43-C4, es seguridad: cortar el acceso de un tercero no espera a la
// aprobación (ver `admite_retiro_de_especialistas` en el backend)—; lo que faltaba era
// decirlo. Solo se anuncia lo que el servidor permite, así que el texto nunca promete
// una acción que la pantalla no ofrece.
//
// BUG-898 (`ui/MG-ILE-898.md`): POR QUÉ está cerrada lo dice el servidor
// (`motivo_de_no_editable`, derivado del estado del INFORME en la misma unidad de trabajo) y
// se pinta tal cual; lo que sigue abierto se agrega desde los permisos `puede_*` del servidor
// (Vacíos 1 de la spec). Sin el campo (backend anterior a la tarea 898-2) queda el texto que
// la pantalla ya armaba desde el estado del caso.
const motivoDeSoloLectura = (vista: Evaluacion, puedeAmpliar: boolean, puedeSeguirAcciones: boolean, puedeRetirarEspecialistas: boolean): string => {
  const abierto = (puedeAmpliar ? ' La ampliación del informe (act. 8) sigue abierta.' : '')
    + (puedeSeguirAcciones ? ' El estado de las acciones se sigue actualizando hasta el cierre.' : '')
    + (puedeRetirarEspecialistas ? ' Sigue abierto: retirar el acceso de un especialista invitado.' : '');
  if (vista.motivo_de_no_editable) return `${vista.motivo_de_no_editable}${abierto}`;
  if (vista.estado_del_caso === 'en_aprobacion') {
    // «Alta y baja» y no «las acciones» (revision IA del PR 48): con el informe en
    // aprobacion el ESTADO de cada accion se sigue cambiando (C2, backend
    // ESTADOS_CON_SEGUIMIENTO_DE_COMPROMISOS); decir que no se modifican contradice la pestaña.
    return `El informe FAR-03 está en aprobación: la investigación, la causalidad, la gravedad y el alta o baja de acciones no se modifican hasta que el aprobador lo apruebe o lo observe.${abierto}`;
  }
  return `El caso está ${etiquetaDelEstadoDelCaso(vista.estado_del_caso ?? '').toLowerCase()}: la evaluación ya no se edita.${abierto}`;
};

/** La cabecera del caso: bandera, número, estado, el candado de solo lectura y la gravedad decidida. */
function CabeceraDelCaso({ vista, casoId, soloLectura, conAlgoAbierto }: Readonly<{
  vista: Evaluacion; casoId: string; soloLectura: boolean;
  /** Con la ampliación o el seguimiento de acciones abiertos, el candado lo dice. */
  conAlgoAbierto: boolean;
}>) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {vista.pais_evento && <Bandera cod={vista.pais_evento} />}
      <Mono className="font-semibold">{vista.numero_de_caso ?? casoId.slice(0, 8)}</Mono>
      {vista.estado_del_caso && <StatusBadge data-testid="evaluacion-estado-del-caso" tone="info">{etiquetaDelEstadoDelCaso(vista.estado_del_caso)}</StatusBadge>}
      {soloLectura && <StatusBadge data-testid="evaluacion-solo-lectura" tone="neutral" icon={Lock}>{conAlgoAbierto ? 'Solo lectura (salvo lo abierto)' : 'Solo lectura'}</StatusBadge>}
      {vista.gravedad && <StatusBadge tone={vista.gravedad === 'grave' ? 'danger' : 'neutral'}>{vista.gravedad === 'grave' ? 'Grave' : 'No grave'}</StatusBadge>}
    </span>
  );
}

/** La evaluación que no abre: un 403 es falta de acceso (aviso); el resto, una avería. */
function EvaluacionQueNoAbre({ fallo, onVolver }: Readonly<{ fallo: Fallo; onVolver: () => void }>) {
  const sinPermiso = fallo.estado === 403;
  return (
    <>
      <PageHeader eyebrow="Comité · Evaluación del caso" hu="HU-12 · HU-15 · HU-43" title="Evaluación del caso" icon={Stethoscope} actions={<Button variant="secondary" size="sm" icon={ArrowLeft} onClick={onVolver}>Volver</Button>} />
      <Alert data-testid={sinPermiso ? 'evaluacion-sin-permiso' : 'evaluacion-error'} tone={sinPermiso ? 'warning' : 'danger'} title={sinPermiso ? 'No tienes acceso a este caso' : 'No se pudo abrir la evaluación'} detalleTecnico={fallo.detalleTecnico}>
        {fallo.message}
      </Alert>
    </>
  );
}

export default function EvaluacionDelCaso({ casoId, actor, esInvitado = false, puedeAprobar = false, puedeDecidir = puedeAprobar, onDecidido, pestanaInicial = 'investigacion', onVolver, onVerExpediente }: {
  casoId: string;
  /** Quién firma lo que se guarda (el nombre de la sesión). */
  actor: string;
  /** HU-43-C7: la sesión es de un especialista invitado. Solo lectura + observación y adjunto. */
  esInvitado?: boolean;
  /**
   * Abierta desde la cola de aprobación (HU-17, `ui/MG-ILE-43.md`): la evaluación en solo
   * lectura y, en «Informe FAR-03», la versión congelada con la tarjeta «Aprobación».
   */
  puedeAprobar?: boolean;
  /**
   * La sesión es del aprobador (VP o Gerencia). Con la sesión simulada el servidor da todos
   * los roles, así que esta máscara impide que el Comité o el administrador vean «Aprobar»
   * entrando por la cola (HU-17-C9). Solo resta: lo que se ofrece sigue saliendo del servidor.
   */
  puedeDecidir?: boolean;
  /** Tras aprobar u observar: vuelve a la cola con el aviso de éxito. */
  onDecidido?: (d: DecisionDelAprobador) => void;
  /** La pestaña con la que se abre: la bandeja manda a la que resuelve la acción pendiente. */
  pestanaInicial?: PestanaDeLaEvaluacion;
  onVolver: () => void;
  onVerExpediente?: (casoId: string) => void;
}) {
  const [evaluacion, setEvaluacion] = useState<Evaluacion | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [pestana, setPestana] = useState<string>(pestanaInicial);
  const zona = useZonaDelPais()(evaluacion?.pais_evento);
  // HU-11-C5: el informe, solo para el banner de «Observado por el aprobador».
  const [informe, setInforme] = useState<Informe | null>(null);
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  const recargar = useCallback(() => {
    verEvaluacion(casoId)
      .then((e) => { if (vivo.current) setEvaluacion(e); })
      .catch((e) => { if (vivo.current) setFallo(aFallo(e)); });
  }, [casoId]);

  /**
   * Recarga hasta que el ESTADO DEL CASO deje de ser el que era, o hasta agotar tres
   * intentos cortos.
   *
   * POR QUÉ NO BASTA CON RECARGAR UNA VEZ (25-sep-2026, reportado sobre PE-223 y BO-096).
   * Enviar el FAR-03 a aprobación NO mueve el caso en esa misma petición: publica un
   * hecho y es el contexto `casos` quien, al consumirlo, lo mueve. La recarga inmediata
   * llega antes que ese consumo, así que la tarjeta decía «En aprobación» y la insignia
   * del encabezado seguía diciendo «En evaluación» — dos verdades a la vez. Medido en el
   * publicado: entre 18 y 42 segundos, que es lo que tardaba en cuadrarse al cambiar de
   * pestaña y volver.
   *
   * ESTO NO ADIVINA EL ESTADO NUEVO, LO ESPERA. Se pinta siempre lo que responde el
   * servidor; lo único que se añade es no conformarse con la primera respuesta si todavía
   * trae el valor viejo. Deducir el estado a partir del informe metería la máquina de
   * estados del caso dentro de la pantalla, que es donde peor envejece.
   *
   * LA VENTANA LLEGA A OCHO SEGUNDOS, Y ESTÁ MEDIDA. Empezó en 1,2 s dando por hecho que
   * el drenado tardaría milisegundos; en el sobre de PE-224-2026 se midieron 6,59 s y el
   * bucle se rendía antes. La causa es el entorno, no el código: un backend local habla
   * con la base de Azure, así que cada ida y vuelta cruza internet y el drenado hace
   * muchas. En el publicado, con la API al lado de la base, basta con los primeros
   * intentos — por eso las esperas empiezan cortas y sólo crecen si hace falta.
   *
   * Y NO SE BLOQUEA LA ACCIÓN PARA CONSEGUIRLO. Que la ruta aguardara al drenado antes
   * de responder daría el cambio «a la vez», pero movería esos segundos al botón: se
   * miraría girar un spinner en vez de una insignia que se cuadra sola. Aquí la pantalla
   * sigue viva y sólo refresca cuando hay algo nuevo que decir.
   *
   * Si aun así no llegara, se deja lo último que dijo el servidor —nunca un invento— y
   * la siguiente carga de la pantalla lo cuadra.
   */
  const recargarEsperandoCambioDeEstado = useCallback(async (estadoPrevio: string | null) => {
    for (const espera of ESPERAS_HASTA_QUE_EL_CASO_CAMBIE) {
      if (espera) await new Promise((seguir) => { setTimeout(seguir, espera); });
      if (!vivo.current) return;
      try {
        const e = await verEvaluacion(casoId);
        if (!vivo.current) return;
        setEvaluacion(e);
        if ((e.estado_del_caso ?? null) !== estadoPrevio) return;
      } catch (e) {
        if (vivo.current) setFallo(aFallo(e));
        return;
      }
    }
  }, [casoId]);

  useEffect(() => {
    let vigente = true;
    verEvaluacion(casoId).then((e) => { if (vigente) setEvaluacion(e); }).catch((e) => { if (vigente) setFallo(aFallo(e)); });
    return () => { vigente = false; };
  }, [casoId]);

  const estadoDelCaso = evaluacion?.estado_del_caso ?? null;
  useEffect(() => {
    if (!estadoDelCaso || !ESTADOS_CON_INFORME_EN_REVISION.has(estadoDelCaso)) return undefined;
    let vigente = true;
    verInforme(casoId).then((i) => { if (vigente) setInforme(i); }).catch(() => { /* sin informe: no hay banner */ });
    return () => { vigente = false; };
  }, [casoId, estadoDelCaso]);

  // D2: lo que responde una mutación se FUSIONA con lo que había y después se recarga.
  const alCambiar = useCallback((nueva: Evaluacion) => {
    setEvaluacion((previa) => fusionar(previa, nueva));
    recargar();
  }, [recargar]);

  // CAMBIOS SIN GUARDAR (18-sep-2026). Cada pestaña con formulario dice si tiene algo
  // escrito y no guardado; salir de ella —a otra pestaña o con «Volver»— pide confirmar
  // en vez de descartarlo en silencio. Al entrar a «Informe FAR-03» se recarga la
  // evaluación para que la lista de faltantes sea la del servidor en ese momento.
  const [sucias, setSucias] = useState<Record<string, boolean>>({});
  const marcarSucia = useCallback((clave: string, sucio: boolean) =>
    setSucias((s) => (Boolean(s[clave]) === sucio ? s : { ...s, [clave]: sucio })), []);
  const alSuciaInvestigacion = useCallback((sucio: boolean) => marcarSucia('investigacion', sucio), [marcarSucia]);
  const alSuciaCausalidad = useCallback((sucio: boolean) => marcarSucia('causalidad', sucio), [marcarSucia]);
  const alSuciaGravedad = useCallback((sucio: boolean) => marcarSucia('gravedad', sucio), [marcarSucia]);
  const alSuciaAcciones = useCallback((sucio: boolean) => marcarSucia('acciones', sucio), [marcarSucia]);
  const alSuciaAmpliacion = useCallback((sucio: boolean) => marcarSucia('ampliacion', sucio), [marcarSucia]);
  // A dónde quería ir el usuario cuando se le pidió confirmar: una pestaña o «Volver».
  const [salidaPendiente, setSalidaPendiente] = useState<string | null>(null);
  const irA = (destino: string) => {
    if (destino === 'informe') recargar();
    setPestana(destino);
  };
  const cambiarPestana = (destino: string) => {
    if (destino === pestana) return;
    if (sucias[pestana]) { setSalidaPendiente(destino); return; }
    irA(destino);
  };
  const volver = () => {
    if (sucias[pestana]) { setSalidaPendiente('volver'); return; }
    onVolver();
  };
  const salirSinGuardar = () => {
    const destino = salidaPendiente;
    setSalidaPendiente(null);
    setSucias((s) => ({ ...s, [pestana]: false }));
    if (destino === 'volver') onVolver();
    else if (destino) irA(destino);
  };

  if (fallo) return <EvaluacionQueNoAbre fallo={fallo} onVolver={onVolver} />;
  if (!evaluacion) return <p data-testid="evaluacion-cargando" className="p-6 text-body text-ink-soft">Abriendo la evaluación…</p>;

  // El invitado —y el aprobador— no pueden editar aunque el backend de dev lo permitiera.
  const vista = vistaPara(evaluacion, esInvitado || puedeAprobar);
  const soloLectura = !vista.puede_editar;
  // C1/C2 (21-sep-2026): con el caso fuera del Comité la evaluación se lee, pero la actividad 8
  // (HU-49-C7) y el estado de los compromisos (C-08) siguen abiertos si el servidor lo dice.
  const puedeAmpliar = vista.puede_ampliar === true;
  const puedeSeguirAcciones = vista.puede_seguir_acciones === true;
  // O-04: retirar a un invitado también es «algo abierto» (HU-43-C4); `vistaPara` ya lo
  // apaga para el invitado y el aprobador.
  const puedeRetirarEspecialistas = vista.puede_retirar_especialistas === true;
  const porQueSoloLectura = soloLectura && !esInvitado && !puedeAprobar
    ? motivoDeSoloLectura(vista, puedeAmpliar, puedeSeguirAcciones, puedeRetirarEspecialistas)
    : null;
  const cabecera: ReactNode = <CabeceraDelCaso vista={vista} casoId={casoId} soloLectura={soloLectura} conAlgoAbierto={puedeAmpliar || puedeSeguirAcciones || puedeRetirarEspecialistas} />;

  const investigacionCompleta = vista.total > 0 && vista.respondidos >= vista.total;
  const pestanas: Pestana[] = [
    { key: 'investigacion', label: 'Investigación (29 puntos)', icon: ClipboardList, count: `${vista.respondidos}/${vista.total}`, hecha: investigacionCompleta },
    { key: 'causalidad', label: 'Causalidad y conclusiones', icon: Scale, hecha: Boolean(vista.grado_causalidad) },
    { key: 'gravedad', label: 'Gravedad', icon: AlertTriangle, hecha: vista.gravedad !== null },
    { key: 'acciones', label: 'Acciones', icon: ListChecks, count: vista.acciones.length || undefined },
    { key: 'especialistas', label: 'Especialistas', icon: Users, count: vista.invitaciones.filter((i) => i.vigente).length || undefined },
    { key: 'informe', label: 'Informe FAR-03', icon: FileText },
  ];
  if (vista.ampliacion_habilitada) pestanas.push(PESTANA_AMPLIACION);

  // HU-11-C5: el aprobador devolvió el informe; se dice arriba, con el motivo y el atajo.
  const observacionVigente = observacionVigenteDe(informe, vista.estado_del_caso ?? null);

  return (
    <>
      <PageHeader
        data-testid="evaluacion-cabecera"
        eyebrow={puedeAprobar ? 'Aprobación · Evaluación del caso' : 'Comité · Evaluación del caso'}
        hu="HU-12 · HU-15 · HU-43"
        title="Evaluación del caso"
        subtitle={cabecera}
        icon={Stethoscope}
        actions={
          <>
            {/* EL INVITADO TAMBIEN ENTRA (25-sep-2026, reportado por QA).
                Estaba oculto con `!esInvitado`, razonado como «su acceso es a este caso,
                no al modulo de expedientes». La distincion no la hace la HU: RN-31 dice
                que la invitacion «da solo lectura A ESE CASO Y SOLO A ESE», y C1 lo
                repite —«puede abrir PE-001-2026 en solo lectura»—. El Formato Verde es
                de ese caso, y C10 ya deja el adjunto del propio invitado en ese mismo
                expediente: podia escribir ahi y no podia leerlo.
                Lo que SI conserva la distincion es la llave: el servidor le abre el
                expediente por su INVITACION, no por su pais (ver `rutas.py` de
                expedientes). Un documento L2 le sigue devolviendo 403. */}
            {onVerExpediente && <Button variant="secondary" size="sm" icon={FileUp} onClick={() => onVerExpediente(casoId)}>Formato Verde y evidencias</Button>}
            {puedeAprobar
              ? <Button data-testid="aprobacion-volver" variant="ghost" size="sm" icon={ArrowLeft} onClick={volver}>Volver a la cola</Button>
              : <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={volver}>Volver</Button>}
          </>
        }
      />
      {observacionVigente && !puedeAprobar && (
        <div className="mb-3">
          <Alert tone="warning" title={`Observado por el aprobador (ronda ${observacionVigente.iteracion}): ${observacionVigente.motivo}`}>
            {observacionVigente.observado_por} · {fecha(observacionVigente.observado_en, zona)}. Corrige lo observado y reenvía el informe: será la versión siguiente.
            {pestana !== 'informe' && (
              <> <Button size="sm" variant="secondary" icon={FileText} onClick={() => cambiarPestana('informe')}>Ir al informe</Button></>
            )}
          </Alert>
        </div>
      )}
      {/* HU-49-C5: el aviso literal del servidor; el caso sigue en la cola con «Ampliar informe». */}
      {porQueSoloLectura && (
        <div className="mb-3">
          <Alert data-testid="evaluacion-aviso-solo-lectura" tone="info" title="Evaluación en solo lectura" icon={Lock}>{porQueSoloLectura}</Alert>
        </div>
      )}
      {vista.ampliacion_pendiente && (
        <div className="mb-3">
          <Alert tone="warning" title={vista.aviso_de_ampliacion ?? 'Falta la información adicional de la actividad 8'}>
            El FAR-03 de un caso grave sale señalado hasta que el Comité amplíe el informe.
            {vista.ampliacion_habilitada && (!soloLectura || puedeAmpliar) && (
              <> <Button size="sm" variant="secondary" icon={FileUp} onClick={() => cambiarPestana('ampliacion')}>Ampliar informe (act. 8)</Button></>
            )}
          </Alert>
        </div>
      )}
      <Modal open={salidaPendiente !== null} onClose={() => setSalidaPendiente(null)} tone="warning" icon={AlertTriangle}
        title={`Tienes cambios sin guardar en «${pestanas.find((p) => p.key === pestana)?.label ?? pestana}»`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setSalidaPendiente(null)}>Seguir editando</Button>
            <Button variant="danger" onClick={salirSinGuardar}>Salir sin guardar</Button>
          </>
        }>
        <p className="text-body">
          Si sales ahora, se pierde lo que escribiste. Para conservarlo, vuelve y guarda antes de cambiar de pestaña.
        </p>
      </Modal>
      <RecargarLaEvaluacion.Provider value={recargar}>
      <Tabs tabs={pestanas} active={pestana} onChange={cambiarPestana} aria-label="Secciones de la evaluación">
        <div className="mt-4">
          {pestana === 'investigacion' && (soloLectura
            ? <InstructivoSoloLectura evaluacion={vista} />
            : <PestanaInvestigacion evaluacion={vista} onCambio={alCambiar} actor={actor} onSucio={alSuciaInvestigacion} />)}
          {pestana === 'causalidad' && <PestanaCausalidad key={vista.evaluacion_id} evaluacion={vista} onCambio={alCambiar} actor={actor} onSucio={alSuciaCausalidad} />}
          {pestana === 'gravedad' && <PestanaGravedad evaluacion={vista} onCambio={alCambiar} actor={actor} onSucio={alSuciaGravedad} onIrACausalidad={() => cambiarPestana('causalidad')} />}
          {pestana === 'acciones' && <PestanaAcciones evaluacion={vista} onCambio={alCambiar} onSucio={alSuciaAcciones} />}
          {pestana === 'especialistas' && <PestanaEspecialistas evaluacion={vista} onCambio={alCambiar} actor={actor} esInvitado={esInvitado} />}
          {pestana === 'informe' && puedeAprobar && (
            <AprobacionDelInforme casoId={casoId} zonaHoraria={zona} puedeDecidir={puedeDecidir} onVolver={onVolver}
              onDecidido={(d) => (onDecidido ? onDecidido(d) : onVolver())} />
          )}
          {pestana === 'informe' && !puedeAprobar && (
            // Lo que se ofrece en el informe sale de `acciones_disponibles` (§8.1 R4); la
            // máscara de la sesión solo apaga al invitado, que nunca escribe en el informe.
            <InformeFar03 casoId={casoId} zonaHoraria={zona} actor={actor} soloLectura={esInvitado}
              topeDeSeccion={topeDeTexto(vista, 'redaccion_seccion_caracteres')}
              faltantes={vista.faltantes_para_el_informe} onIrAEvaluacion={(p) => cambiarPestana(p ?? 'causalidad')}
              onCambio={(i) => {
                setInforme(i);
                // El estado del caso cambia por evento, no en esta petición: se espera a
                // que cambie en vez de conformarse con la primera respuesta.
                // Atrapa sus propios errores (los muestra en el aviso de fallo).
                recargarEsperandoCambioDeEstado(vista.estado_del_caso ?? null).catch(() => {});
              }} />
          )}
          {pestana === 'ampliacion' && vista.ampliacion_habilitada && (
            <AmpliacionDelInforme casoId={casoId} actor={actor} soloLectura={soloLectura && !puedeAmpliar} evaluacion={vista} zonaHoraria={zona}
              onCambio={recargar} onSucio={alSuciaAmpliacion} />
          )}
        </div>
      </Tabs>
      </RecargarLaEvaluacion.Provider>
    </>
  );
}
